// ============================================================
// SSX SOVEREIGN TERMINAL — v3.0.0
// PROMPT 3 OF 4: THE EXCHANGE & INTEREST TRANSFER MARKET
// ExchangeView — Full Implementation
// All prior architecture (Prompts 1 & 2) preserved verbatim.
// ============================================================

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";

// ─────────────────────────────────────────────────────────────
// § 0. GLOBAL CSS — TACTICAL NOIR (Prompts 1 & 2 — Preserved)
//     + New additions for Exchange interaction patterns
// ─────────────────────────────────────────────────────────────
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:ital,wght@0,400;0,700;1,400&display=swap');

  :root {
    --emerald: #00ff88;
    --emerald-dim: rgba(0, 255, 136, 0.12);
    --emerald-border: rgba(0, 255, 136, 0.18);
    --abyss: #040a0f;
    --abyss-mid: #07111a;
    --abyss-surface: #0b1822;
    --border: rgba(0, 255, 136, 0.12);
    --border-dim: rgba(255, 255, 255, 0.06);
    --text-primary: rgba(255,255,255,0.92);
    --text-secondary: rgba(255,255,255,0.55);
    --text-muted: rgba(255,255,255,0.28);
    --red-signal: #ff4466;
    --gold-signal: #f5c842;
    --amber-signal: #f59e0b;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  html, body, #root {
    height: 100%; width: 100%;
    background: var(--abyss);
    color: var(--text-primary);
    overflow: hidden;
  }

  .mono { font-family: 'Space Mono', 'Courier New', monospace; }

  .glass {
    background: rgba(255,255,255,0.03);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
  .glass-dark {
    background: rgba(4, 10, 15, 0.72);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid var(--border-dim);
  }
  .glass-emerald {
    background: rgba(0, 255, 136, 0.04);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid var(--emerald-border);
  }
  .glass-amber {
    background: rgba(245, 158, 11, 0.04);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid rgba(245, 158, 11, 0.18);
  }
  .glass-modal {
    background: rgba(4, 10, 15, 0.97);
    backdrop-filter: blur(32px);
    -webkit-backdrop-filter: blur(32px);
    border: 1px solid var(--emerald-border);
  }
  .glass-sidebar {
    background: rgba(4, 10, 15, 0.92);
    border-right: 1px solid var(--border);
  }
  .glass-topbar {
    background: rgba(4, 10, 15, 0.96);
    border-bottom: 1px solid var(--border);
  }

  .corner-box {
    position: relative;
    border: 1px solid var(--border-dim);
  }
  .corner-box::before,
  .corner-box::after {
    content: '';
    position: absolute;
    width: 10px; height: 10px;
    border-color: var(--emerald);
    border-style: solid;
    opacity: 0.55;
    z-index: 10;
  }
  .corner-box::before { top: -1px; left: -1px; border-width: 1px 0 0 1px; }
  .corner-box::after  { bottom: -1px; right: -1px; border-width: 0 1px 1px 0; }

  .verdict-box {
    position: relative;
    background: rgba(0, 255, 136, 0.04);
    border: 1px solid var(--emerald-border);
    padding: 1.25rem;
    border-radius: 2px;
  }

  .glow-line {
    width: 100%; height: 1px;
    background: linear-gradient(90deg, transparent, var(--emerald), transparent);
    opacity: 0.3;
  }
  .glow-line-amber {
    width: 100%; height: 1px;
    background: linear-gradient(90deg, transparent, var(--amber-signal), transparent);
    opacity: 0.3;
  }
  .glow-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--emerald);
    box-shadow: 0 0 8px var(--emerald), 0 0 16px rgba(0,255,136,0.4);
    flex-shrink: 0;
  }
  .glow-dot-red {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--red-signal);
    box-shadow: 0 0 8px var(--red-signal);
    flex-shrink: 0;
  }
  .glow-dot-gold {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--gold-signal);
    box-shadow: 0 0 8px var(--gold-signal);
    flex-shrink: 0;
  }
  .glow-dot-amber {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--amber-signal);
    box-shadow: 0 0 8px var(--amber-signal);
    flex-shrink: 0;
  }
  .glow-dot-muted {
    width: 6px; height: 6px; border-radius: 50%;
    background: rgba(255,255,255,0.2);
    flex-shrink: 0;
  }

  @keyframes pulse-emerald {
    0%, 100% { opacity: 1; box-shadow: 0 0 8px var(--emerald), 0 0 16px rgba(0,255,136,0.4); }
    50%       { opacity: 0.45; box-shadow: 0 0 4px var(--emerald); }
  }
  .pulse { animation: pulse-emerald 2.2s ease-in-out infinite; }

  @keyframes pulse-amber {
    0%, 100% { opacity: 1; box-shadow: 0 0 8px var(--amber-signal); }
    50%       { opacity: 0.4; box-shadow: 0 0 3px var(--amber-signal); }
  }
  .pulse-amber { animation: pulse-amber 2.8s ease-in-out infinite; }

  /* Slot fill pip animation */
  @keyframes pip-in {
    0%   { transform: scale(0); opacity: 0; }
    60%  { transform: scale(1.25); }
    100% { transform: scale(1); opacity: 1; }
  }
  .pip-in { animation: pip-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }

  @keyframes ticker-scroll {
    0%   { transform: translateX(100vw); }
    100% { transform: translateX(-100%); }
  }
  .ticker-track { animation: ticker-scroll 38s linear infinite; white-space: nowrap; }

  /* Activity feed row */
  @keyframes feed-in {
    from { opacity: 0; transform: translateX(-6px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  /* Exchange CTA Buttons */
  .btn-allocate {
    position: relative;
    overflow: hidden;
    cursor: pointer;
    border: 1px solid rgba(0,255,136,0.35);
    background: rgba(0,255,136,0.07);
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--emerald);
    padding: 10px 20px;
    width: 100%;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .btn-allocate:hover {
    background: rgba(0,255,136,0.14);
    border-color: rgba(0,255,136,0.6);
    box-shadow: 0 0 24px rgba(0,255,136,0.12), inset 0 0 16px rgba(0,255,136,0.06);
    letter-spacing: 0.24em;
  }
  /* Sweep shimmer on hover */
  .btn-allocate::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(105deg, transparent 40%, rgba(0,255,136,0.1) 50%, transparent 60%);
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  .btn-allocate:hover::after {
    opacity: 1;
    animation: shimmer 1.2s ease infinite;
  }

  /* Fully subscribed — disabled state */
  .btn-subscribed {
    cursor: not-allowed;
    border: 1px solid rgba(255,255,255,0.08);
    background: rgba(255,255,255,0.02);
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.22);
    padding: 10px 20px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  /* Queue Button */
  .btn-queue {
    cursor: pointer;
    border: 1px solid rgba(245,158,11,0.22);
    background: rgba(245,158,11,0.04);
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--amber-signal);
    padding: 10px 20px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s ease;
  }
  .btn-queue:hover {
    background: rgba(245,158,11,0.09);
    border-color: rgba(245,158,11,0.4);
    box-shadow: 0 0 16px rgba(245,158,11,0.08);
  }

  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }

  /* Modal overlay */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(4, 10, 15, 0.88);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }

  /* Form inputs */
  .terminal-input {
    width: 100%;
    background: rgba(4,10,15,0.8);
    border: 1px solid rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.85);
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 10px 14px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    border-radius: 1px;
  }
  .terminal-input::placeholder {
    color: rgba(255,255,255,0.2);
    font-size: 10px;
    letter-spacing: 0.12em;
  }
  .terminal-input:focus {
    border-color: rgba(0,255,136,0.35);
    box-shadow: 0 0 0 1px rgba(0,255,136,0.1);
    background: rgba(0,255,136,0.025);
  }
  .terminal-select {
    width: 100%;
    background: rgba(4,10,15,0.8);
    border: 1px solid rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.85);
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 10px 14px;
    outline: none;
    cursor: pointer;
    appearance: none;
    transition: border-color 0.2s ease;
  }
  .terminal-select:focus {
    border-color: rgba(0,255,136,0.35);
    box-shadow: 0 0 0 1px rgba(0,255,136,0.1);
  }
  /* Submit CTA */
  .btn-submit {
    cursor: pointer;
    border: 1px solid rgba(0,255,136,0.4);
    background: rgba(0,255,136,0.09);
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.24em;
    text-transform: uppercase;
    color: var(--emerald);
    padding: 12px 24px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .btn-submit:hover {
    background: rgba(0,255,136,0.16);
    border-color: rgba(0,255,136,0.7);
    box-shadow: 0 0 28px rgba(0,255,136,0.14);
  }
  .btn-cancel {
    cursor: pointer;
    border: 1px solid rgba(255,255,255,0.08);
    background: transparent;
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.3);
    padding: 12px 24px;
    transition: all 0.18s ease;
  }
  .btn-cancel:hover {
    border-color: rgba(255,255,255,0.18);
    color: rgba(255,255,255,0.55);
  }

  .card-hover {
    transition: border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
  }
  .card-hover:hover {
    border-color: var(--emerald-border) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 24px rgba(0,255,136,0.06);
  }

  .data-table { width: 100%; border-collapse: collapse; }
  .data-table th {
    text-align: left;
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-muted);
    padding: 10px 20px 8px;
    border-bottom: 1px solid var(--border-dim);
  }
  .data-table td {
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    text-transform: uppercase;
    color: var(--text-secondary);
    padding: 10px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
  }
  .data-table tr:last-child td { border-bottom: none; }
  .data-table tr:hover td { background: rgba(0,255,136,0.025); }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }

  .badge-phase {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(0,255,136,0.08);
    border: 1px solid var(--emerald-border);
    padding: 3px 10px;
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--emerald);
    border-radius: 2px;
  }

  .nav-item {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 16px;
    cursor: pointer;
    border-radius: 2px;
    transition: background 0.2s ease, border-color 0.2s ease;
    border: 1px solid transparent;
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 2px;
    position: relative;
  }
  .nav-item:hover {
    background: rgba(0,255,136,0.05);
    color: var(--text-secondary);
    border-color: var(--border);
  }
  .nav-item.active {
    background: rgba(0,255,136,0.08);
    border-color: var(--emerald-border);
    color: var(--emerald);
  }
  .nav-item.active::before {
    content: '';
    position: absolute;
    left: 0; top: 20%; bottom: 20%;
    width: 2px;
    background: var(--emerald);
    border-radius: 0 2px 2px 0;
    box-shadow: 0 0 8px var(--emerald);
  }

  .telemetry-chip {
    display: flex; align-items: center; gap: 6px;
    padding: 3px 10px;
    border: 1px solid var(--border-dim);
    background: rgba(255,255,255,0.02);
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-muted);
  }

  .scanline-overlay {
    pointer-events: none;
    position: fixed; inset: 0;
    background: repeating-linear-gradient(
      to bottom, transparent, transparent 2px,
      rgba(0,0,0,0.04) 2px, rgba(0,0,0,0.04) 4px
    );
    z-index: 9999;
    opacity: 0.35;
  }

  .terminal-grid {
    background-image:
      linear-gradient(rgba(0,255,136,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,255,136,0.025) 1px, transparent 1px);
    background-size: 40px 40px;
  }

  @keyframes decrypt {
    0%   { opacity: 0.3; letter-spacing: 0.4em; filter: blur(2px); }
    100% { opacity: 1;   letter-spacing: 0.14em; filter: blur(0); }
  }
  .decrypt-anim { animation: decrypt 0.8s ease forwards; }

  .doc-row {
    transition: background 0.2s ease;
    border-bottom: 1px solid rgba(255,255,255,0.04);
  }
  .doc-row:last-child { border-bottom: none; }
  .doc-row:hover { background: rgba(0,255,136,0.02); }

  .btn-secure {
    position: relative; overflow: hidden; cursor: pointer;
    border: 1px solid var(--emerald-border);
    background: rgba(0,255,136,0.05);
    font-family: 'Space Mono', monospace;
    font-size: 9px; letter-spacing: 0.2em; text-transform: uppercase;
    color: var(--emerald); padding: 8px 16px;
    transition: background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .btn-secure:hover {
    background: rgba(0,255,136,0.12);
    border-color: rgba(0,255,136,0.45);
    box-shadow: 0 0 18px rgba(0,255,136,0.15), inset 0 0 12px rgba(0,255,136,0.05);
  }
  .btn-secure::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(105deg, transparent 40%, rgba(0,255,136,0.12) 50%, transparent 60%);
    background-size: 200% 100%; opacity: 0; transition: opacity 0.2s ease;
  }
  .btn-secure:hover::after {
    opacity: 1;
    animation: shimmer 1.4s ease infinite;
  }
  .btn-secure-amber {
    border-color: rgba(245,158,11,0.25);
    background: rgba(245,158,11,0.04);
    color: var(--amber-signal);
  }
  .btn-secure-amber:hover {
    background: rgba(245,158,11,0.1);
    border-color: rgba(245,158,11,0.45);
    box-shadow: 0 0 18px rgba(245,158,11,0.12);
  }

  /* Activity feed entry */
  .feed-entry {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 10px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    transition: background 0.18s ease;
  }
  .feed-entry:last-child { border-bottom: none; }
  .feed-entry:hover { background: rgba(0,255,136,0.02); }

  /* Scarcity bar */
  @keyframes scarcity-pulse {
    0%, 100% { box-shadow: 0 0 6px var(--red-signal); }
    50%       { box-shadow: 0 0 14px var(--red-signal), 0 0 28px rgba(255,68,102,0.3); }
  }
  .scarcity-glow { animation: scarcity-pulse 1.8s ease-in-out infinite; }

  /* Vault card "LIVE" badge flicker */
  @keyframes live-flicker {
    0%, 95%, 100% { opacity: 1; }
    96%            { opacity: 0.4; }
    98%            { opacity: 0.9; }
  }
  .live-flicker { animation: live-flicker 4s ease-in-out infinite; }
`;

// ─────────────────────────────────────────────────────────────
// § 1. FRAMER MOTION PHYSICS (Preserved)
// ─────────────────────────────────────────────────────────────
export const STIFF_INSTITUTIONAL = {
  type: "spring",
  stiffness: 400,
  damping: 40,
  mass: 1,
};
export const SPRING_HEAVY = {
  type: "spring",
  stiffness: 280,
  damping: 32,
  mass: 1.2,
};
export const FADE_VIEWPORT = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { ...SPRING_HEAVY } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.18 } },
};
const STAGGER_CONTAINER = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.08 } },
};
const STAGGER_CHILD = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { ...SPRING_HEAVY } },
};
const STAGGER_CHILD_LEFT = {
  hidden: { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0, transition: { ...STIFF_INSTITUTIONAL } },
};

// ─────────────────────────────────────────────────────────────
// § 2. UTILITY
// ─────────────────────────────────────────────────────────────
function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}
const fmt = {
  usd: (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n),
  pct: (n, d = 2) => `${(n * 100).toFixed(d)}%`,
  date: (iso) => {
    const d = new Date(iso);
    const months = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];
    return `${months[d.getMonth()]} ${String(d.getDate()).padStart(
      2,
      "0"
    )}, ${d.getFullYear()}`;
  },
};

// ─────────────────────────────────────────────────────────────
// § 3. MICRO-COMPONENT LIBRARY (Preserved)
// ─────────────────────────────────────────────────────────────
export const CornerBox = ({ children, className = "", style = {} }) => (
  <div className={cn("corner-box", className)} style={style}>
    {children}
  </div>
);

export const Label = ({ children, className = "" }) => (
  <div
    className={cn("mono uppercase tracking-widest text-white/40", className)}
    style={{ fontSize: "9px", letterSpacing: "0.2em" }}
  >
    {children}
  </div>
);

export const Dot = ({ color = "emerald", pulse = false }) => {
  const cls = {
    emerald: "glow-dot",
    red: "glow-dot-red",
    gold: "glow-dot-gold",
    amber: "glow-dot-amber",
    muted: "glow-dot-muted",
  };
  return (
    <div
      className={cn(
        cls[color] || "glow-dot",
        pulse && color === "emerald" ? "pulse" : "",
        pulse && color === "amber" ? "pulse-amber" : ""
      )}
    />
  );
};

export const PhaseBadge = ({ children }) => (
  <span className="badge-phase">
    <Dot color="emerald" pulse />
    {children}
  </span>
);

export const StatusBadge = ({ children, type = "green" }) => {
  const typeStyles = {
    green: "bg-emerald-500/10 border-emerald-500/25 text-emerald-400",
    red: "bg-red-500/10 border-red-500/25 text-red-400",
    gold: "bg-yellow-500/10 border-yellow-500/25 text-yellow-400",
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-400",
    blue: "bg-blue-500/10 border-blue-500/25 text-blue-400",
    muted: "bg-white/5 border-white/10 text-white/40",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 border mono uppercase",
        "text-[9px] tracking-[0.15em] rounded-sm",
        typeStyles[type] || typeStyles.muted
      )}
    >
      {children}
    </span>
  );
};

export const VerifiedBadge = ({ label = "VERIFIED" }) => (
  <span
    className="inline-flex items-center gap-1.5 mono uppercase"
    style={{
      fontSize: "9px",
      letterSpacing: "0.18em",
      color: "var(--emerald)",
    }}
  >
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path
        d="M5 0.5L6.18 3.28L9.21 3.5L7.05 5.42L7.74 8.39L5 6.77L2.26 8.39L2.95 5.42L0.79 3.5L3.82 3.28L5 0.5Z"
        fill="currentColor"
        opacity="0.9"
      />
    </svg>
    {label}
  </span>
);

export const MetricCard = ({
  label,
  value,
  sub,
  highlight = false,
  trend,
  accent,
}) => (
  <CornerBox
    className={cn(
      "glass-dark p-4 card-hover",
      highlight ? "glass-emerald" : "",
      accent === "amber" ? "glass-amber" : ""
    )}
  >
    <Label>{label}</Label>
    <div
      className={cn(
        "mono font-black mt-2 uppercase",
        highlight ? "text-[var(--emerald)]" : "",
        accent === "amber" ? "text-[var(--amber-signal)]" : "",
        !highlight && accent !== "amber" ? "text-white" : ""
      )}
      style={{ fontSize: "20px", letterSpacing: "-0.02em" }}
    >
      {value}
    </div>
    {sub && (
      <div
        className="mono text-white/35 uppercase mt-1"
        style={{ fontSize: "9px", letterSpacing: "0.14em" }}
      >
        {sub}
      </div>
    )}
    {trend && (
      <div className="flex items-center gap-1.5 mt-2">
        <Dot color={trend === "up" ? "emerald" : "red"} />
        <span
          className="mono uppercase"
          style={{
            fontSize: "9px",
            color: trend === "up" ? "var(--emerald)" : "var(--red-signal)",
          }}
        >
          {trend === "up" ? "POSITIVE SIGNAL" : "ATTENTION REQUIRED"}
        </span>
      </div>
    )}
  </CornerBox>
);

export const LedgerRow = ({ label, value, sub, highlight = false, status }) => (
  <div
    className={cn(
      "flex items-center justify-between px-4 py-3",
      "border-b border-white/[0.04] last:border-b-0",
      "transition-colors duration-200 hover:bg-[rgba(0,255,136,0.025)]"
    )}
  >
    <div className="flex items-center gap-3">
      <Dot color={highlight ? "emerald" : "gold"} />
      <div>
        <div
          className="mono uppercase text-white/60"
          style={{ fontSize: "10px", letterSpacing: "0.14em" }}
        >
          {label}
        </div>
        {sub && (
          <div
            className="mono uppercase text-white/25 mt-0.5"
            style={{ fontSize: "8px", letterSpacing: "0.12em" }}
          >
            {sub}
          </div>
        )}
      </div>
    </div>
    <div className="flex items-center gap-3">
      {status && <StatusBadge type={status.type}>{status.label}</StatusBadge>}
      <div
        className={cn(
          "mono font-black uppercase",
          highlight ? "text-[var(--emerald)]" : "text-white"
        )}
        style={{ fontSize: "13px" }}
      >
        {value}
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// § 4. SHADOW LEDGER — SOVEREIGN CONTEXT
//     V3.0 update: Added `gav` and `monthlyHAP` to vault data.
// ─────────────────────────────────────────────────────────────
const V2_CONSTANTS = {
  RAISE_PER_VAULT: 300_000,
  LP_MIN_TICKET: 50_000,
  LPS_PER_VAULT: 6,
  ORIGINATION_FEE_PCT: 0.1,
  ORIGINATION_FEE: 30_000,
  NET_YIELD_PCT: 0.125,
  GROSS_REVENUE_ANNUAL: 99_000,
  MONTHLY_HAP: 8_250,
  ASSET_TYPE: "6BR SOVEREIGN FORTRESS",
  HAP_BACKSTOP: "DCHA / HUD · FY2026",
};

const ACTIVE_VAULTS = [
  {
    id: "SSX-VAULT-001",
    shortId: "V001",
    address: "1847 BENNING ROAD NE",
    city: "WASHINGTON, D.C. 20002",
    assetType: V2_CONSTANTS.ASSET_TYPE,
    phase: "OPERATIONAL",
    phaseCode: "OPS",
    raise: V2_CONSTANTS.RAISE_PER_VAULT,
    originationFee: V2_CONSTANTS.ORIGINATION_FEE,
    netYieldPct: V2_CONSTANTS.NET_YIELD_PCT,
    grossRevenueAnnual: V2_CONSTANTS.GROSS_REVENUE_ANNUAL,
    monthlyHAP: V2_CONSTANTS.MONTHLY_HAP,
    hapBackstop: V2_CONSTANTS.HAP_BACKSTOP,
    lpsCommitted: 6,
    lpCapacity: V2_CONSTANTS.LPS_PER_VAULT,
    lpMinTicket: V2_CONSTANTS.LP_MIN_TICKET,
    capitalDeployed: 300_000,
    capitalTarget: 300_000,
    preferredReturnAnnual: 300_000 * V2_CONSTANTS.NET_YIELD_PCT,
    bedrooms: 6,
    sqft: 3_200,
    gav: 719_000,
    hapContractNumber: "HAP-2026-DC-4471",
    closingDate: "2026-01-15",
    lpRoster: [
      {
        id: "LP-001",
        name: "MERIDIAN CAPITAL FUND II",
        committed: 50_000,
        status: "ACTIVE",
      },
      {
        id: "LP-002",
        name: "ATLAS SOVEREIGN PARTNERS",
        committed: 50_000,
        status: "ACTIVE",
      },
      {
        id: "LP-003",
        name: "NORTHGATE FAMILY OFFICE",
        committed: 50_000,
        status: "ACTIVE",
      },
      {
        id: "LP-004",
        name: "IRONVEIL ACCRED. TRUST",
        committed: 50_000,
        status: "ACTIVE",
      },
      {
        id: "LP-005",
        name: "CRESCENT RIDGE HOLDINGS",
        committed: 50_000,
        status: "ACTIVE",
      },
      {
        id: "LP-006",
        name: "SOVEREIGN YIELD FUND I",
        committed: 50_000,
        status: "ACTIVE",
      },
    ],
  },
  {
    id: "SSX-VAULT-002",
    shortId: "V002",
    address: "3312 SHERIFF ROAD NE",
    city: "WASHINGTON, D.C. 20019",
    assetType: V2_CONSTANTS.ASSET_TYPE,
    phase: "FUNDING",
    phaseCode: "FUND",
    raise: V2_CONSTANTS.RAISE_PER_VAULT,
    originationFee: V2_CONSTANTS.ORIGINATION_FEE,
    netYieldPct: V2_CONSTANTS.NET_YIELD_PCT,
    grossRevenueAnnual: V2_CONSTANTS.GROSS_REVENUE_ANNUAL,
    monthlyHAP: V2_CONSTANTS.MONTHLY_HAP,
    hapBackstop: V2_CONSTANTS.HAP_BACKSTOP,
    lpsCommitted: 4,
    lpCapacity: V2_CONSTANTS.LPS_PER_VAULT,
    lpMinTicket: V2_CONSTANTS.LP_MIN_TICKET,
    capitalDeployed: 200_000,
    capitalTarget: 300_000,
    preferredReturnAnnual: 300_000 * V2_CONSTANTS.NET_YIELD_PCT,
    bedrooms: 6,
    sqft: 3_050,
    gav: 685_000,
    hapContractNumber: "HAP-2026-DC-4512",
    closingDate: null,
    lpRoster: [
      {
        id: "LP-001",
        name: "MERIDIAN CAPITAL FUND II",
        committed: 50_000,
        status: "ACTIVE",
      },
      {
        id: "LP-007",
        name: "BLACKRIDGE FAMILY OFFICE",
        committed: 50_000,
        status: "ACTIVE",
      },
      {
        id: "LP-008",
        name: "VECTOR YIELD PARTNERS",
        committed: 50_000,
        status: "ACTIVE",
      },
      {
        id: "LP-009",
        name: "ALTUS SOVEREIGN TRUST",
        committed: 50_000,
        status: "ACTIVE",
      },
    ],
  },
  {
    id: "SSX-VAULT-003",
    shortId: "V003",
    address: "5508 EASTERN AVENUE NE",
    city: "WASHINGTON, D.C. 20011",
    assetType: V2_CONSTANTS.ASSET_TYPE,
    phase: "PIPELINE",
    phaseCode: "PIPE",
    raise: V2_CONSTANTS.RAISE_PER_VAULT,
    originationFee: V2_CONSTANTS.ORIGINATION_FEE,
    netYieldPct: V2_CONSTANTS.NET_YIELD_PCT,
    grossRevenueAnnual: V2_CONSTANTS.GROSS_REVENUE_ANNUAL,
    monthlyHAP: V2_CONSTANTS.MONTHLY_HAP,
    hapBackstop: V2_CONSTANTS.HAP_BACKSTOP,
    lpsCommitted: 0,
    lpCapacity: V2_CONSTANTS.LPS_PER_VAULT,
    lpMinTicket: V2_CONSTANTS.LP_MIN_TICKET,
    capitalDeployed: 0,
    capitalTarget: 300_000,
    preferredReturnAnnual: 300_000 * V2_CONSTANTS.NET_YIELD_PCT,
    bedrooms: 6,
    sqft: 3_400,
    gav: 748_000,
    hapContractNumber: "PENDING · HAP APPLICATION FY2026",
    closingDate: null,
    lpRoster: [],
  },
];

const CURRENT_USER = {
  id: "LP-001",
  displayName: "MERIDIAN CAPITAL FUND II",
  legalName: "Meridian Capital Management Fund II, LP",
  accreditationStatus: "VERIFIED",
  accreditationType: "ACCREDITED INVESTOR · REG D 506(c)",
  kycStatus: "CLEARED",
  totalCommitted: 100_000,
  totalVaults: 2,
  preferredReturnAnnual: 100_000 * V2_CONSTANTS.NET_YIELD_PCT,
  joinDate: "2026-01-10",
  tier: "INSTITUTIONAL",
  contactPrimary: "compliance@meridiancap.fund",
  ein: "47-●●●●●●●",
  liquidAssetTier: "$1,000,000+",
  entityType: "DELAWARE LIMITED PARTNERSHIP",
  cpaFirm: "GRANT THORNTON LLP",
  cpaVerificationExpiry: "2026-12-31",
  amlClearanceDate: "2026-01-08",
  subscribedEntities: ["SSX VAULT SERIES 001 LLC", "SSX VAULT SERIES 002 LLC"],
};

const computeSystemMetrics = (vaults) => {
  const totalAUM = vaults.reduce((s, v) => s + v.capitalDeployed, 0);
  const totalARR = vaults
    .filter((v) => v.phase === "OPERATIONAL")
    .reduce((s, v) => s + v.grossRevenueAnnual, 0);
  const activeYield = totalAUM * V2_CONSTANTS.NET_YIELD_PCT;
  const operationalVaults = vaults.filter(
    (v) => v.phase === "OPERATIONAL"
  ).length;
  const totalLPs = vaults.reduce((s, v) => s + v.lpsCommitted, 0);
  const totalSlots = vaults.reduce((s, v) => s + v.lpCapacity, 0);
  const filledSlots = vaults.reduce((s, v) => s + v.lpsCommitted, 0);
  const avgFillRate = totalSlots > 0 ? filledSlots / totalSlots : 0;
  return {
    totalAUM,
    totalARR,
    activeYield,
    operationalVaults,
    fundingVaults: vaults.filter((v) => v.phase === "FUNDING").length,
    pipelineVaults: vaults.filter((v) => v.phase === "PIPELINE").length,
    totalLPs,
    avgFillRate,
    netYieldPct: V2_CONSTANTS.NET_YIELD_PCT,
    totalVaults: vaults.length,
  };
};

const SovereignContext = createContext(null);
export const useSovereign = () => {
  const ctx = useContext(SovereignContext);
  if (!ctx) throw new Error("useSovereign must be inside <SovereignProvider>");
  return ctx;
};

export const SovereignProvider = ({ children }) => {
  const [activeVaults, setActiveVaults] = useState(ACTIVE_VAULTS);
  const [currentUser] = useState(CURRENT_USER);
  const [systemMetrics, setSystemMetrics] = useState(
    computeSystemMetrics(ACTIVE_VAULTS)
  );
  const [activeView, setActiveView] = useState("EXCHANGE");
  const [systemTime, setSystemTime] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setSystemTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    setSystemMetrics(computeSystemMetrics(activeVaults));
  }, [activeVaults]);

  return (
    <SovereignContext.Provider
      value={{
        activeVaults,
        setActiveVaults,
        currentUser,
        systemMetrics,
        systemTime,
        activeView,
        setActiveView,
        V2_CONSTANTS,
      }}
    >
      {children}
    </SovereignContext.Provider>
  );
};

// ─────────────────────────────────────────────────────────────
// § 5. SHARED VIEW SHELL (Preserved)
// ─────────────────────────────────────────────────────────────
const ViewShell = ({ children, viewKey }) => (
  <motion.div
    key={viewKey}
    variants={FADE_VIEWPORT}
    initial="hidden"
    animate="visible"
    exit="exit"
    className="h-full w-full overflow-y-auto terminal-grid"
    style={{ padding: "28px 32px" }}
  >
    {children}
  </motion.div>
);

// ─────────────────────────────────────────────────────────────
// § 6. EXCHANGE VIEW — THE SOVEREIGN MARKET BOARD
//     v3.0.0 — Full Implementation
//     Live vault allocation grid, scarcity engine,
//     allocation clearance modal, platform activity feed.
// ─────────────────────────────────────────────────────────────

// ── Activity Feed Data ────────────────────────────────────────
// Sorted newest-first. Each entry: timestamp, type, message, vault, detail
const ACTIVITY_FEED = [
  {
    id: "EVT-014",
    ts: "2026-04-14T16:42:00Z",
    type: "LP_INTEREST",
    dot: "amber",
    label: "LP INTEREST RECEIVED",
    message: "UNVERIFIED ACCREDITED INQUIRY · PENDING CLEARANCE REVIEW",
    vault: "SSX-VAULT-002",
    detail: "SLOT 5 OF 6 · $50,000 INTEREST REGISTERED",
  },
  {
    id: "EVT-013",
    ts: "2026-04-12T09:15:00Z",
    type: "HAP_CONFIRMED",
    dot: "emerald",
    label: "HAP PAYMENT CONFIRMED",
    message: "DCHA APRIL 2026 HAP WIRE · RECEIVED IN FULL",
    vault: "SSX-VAULT-001",
    detail: "HAP-2026-DC-4471 · $8,250.00 CLEARED",
  },
  {
    id: "EVT-012",
    ts: "2026-04-10T14:00:00Z",
    type: "LP_SLOT_FILLED",
    dot: "emerald",
    label: "LP SLOT FILLED",
    message: "ALTUS SOVEREIGN TRUST · SUBSCRIPTION EXECUTED",
    vault: "SSX-VAULT-002",
    detail: "LP-009 · $50,000 COMMITTED · SLOT 4/6",
  },
  {
    id: "EVT-011",
    ts: "2026-04-08T11:30:00Z",
    type: "INSPECTION",
    dot: "gold",
    label: "HQS INSPECTION SCHEDULED",
    message: "HUD HOUSING QUALITY STANDARDS FIELD INSPECTION",
    vault: "SSX-VAULT-003",
    detail: "INSPECTOR ID: HUD-DC-882 · MAY 03, 2026",
  },
  {
    id: "EVT-010",
    ts: "2026-04-05T08:00:00Z",
    type: "HAP_APPLICATION",
    dot: "amber",
    label: "HAP APPLICATION SUBMITTED",
    message: "FY2026 HAP CONTRACT APPLICATION FILED WITH DCHA",
    vault: "SSX-VAULT-003",
    detail: "CASE: DCHA-2026-APP-7741 · REVIEW: 30–45 DAYS",
  },
  {
    id: "EVT-009",
    ts: "2026-04-01T07:00:00Z",
    type: "DISTRIBUTION",
    dot: "emerald",
    label: "Q1 DISTRIBUTION PROCESSED",
    message: "Q1 2026 PREFERRED RETURN · ALL LPs PAID",
    vault: "SSX-VAULT-001",
    detail: "6 LPs × $1,562.50 · TOTAL $9,375.00 DISBURSED",
  },
  {
    id: "EVT-008",
    ts: "2026-03-28T13:45:00Z",
    type: "LP_SLOT_FILLED",
    dot: "emerald",
    label: "LP SLOT FILLED",
    message: "VECTOR YIELD PARTNERS · SUBSCRIPTION EXECUTED",
    vault: "SSX-VAULT-002",
    detail: "LP-008 · $50,000 COMMITTED · SLOT 3/6",
  },
  {
    id: "EVT-007",
    ts: "2026-03-20T10:15:00Z",
    type: "ACCREDITATION",
    dot: "emerald",
    label: "ACCREDITATION VERIFIED",
    message: "NEW LP ACCREDITATION CLEARED · CPA CERT ACCEPTED",
    vault: null,
    detail: "INSTITUTIONAL TIER · REG D 506(c) · KYC CLEARED",
  },
  {
    id: "EVT-006",
    ts: "2026-03-15T09:00:00Z",
    type: "TAX",
    dot: "gold",
    label: "COST SEGREGATION DELIVERED",
    message: "YEAR 1 COST SEG STUDY COMPLETED · GRANT THORNTON",
    vault: "SSX-VAULT-001",
    detail: "24 PAGES · ACCELERATED DEPRECIATION CALCULATED",
  },
  {
    id: "EVT-005",
    ts: "2026-02-28T16:00:00Z",
    type: "LP_SLOT_FILLED",
    dot: "emerald",
    label: "LP SLOT FILLED",
    message: "BLACKRIDGE FAMILY OFFICE · SUBSCRIPTION EXECUTED",
    vault: "SSX-VAULT-002",
    detail: "LP-007 · $50,000 COMMITTED · SLOT 2/6",
  },
  {
    id: "EVT-004",
    ts: "2026-02-04T11:00:00Z",
    type: "OPERATING_AGREEMENT",
    dot: "emerald",
    label: "OPERATING AGREEMENT COUNTERSIGNED",
    message: "DELAWARE SERIES LLC · VAULT 002 SERIES EXECUTED",
    vault: "SSX-VAULT-002",
    detail: "SSX VAULT SERIES 002 LLC · 38 PAGES",
  },
  {
    id: "EVT-003",
    ts: "2026-01-20T14:30:00Z",
    type: "HAP_CONFIRMED",
    dot: "emerald",
    label: "HAP CONTRACT EXECUTED",
    message: "DCHA HAP CONTRACT FULLY EXECUTED · PAYMENTS ACTIVE",
    vault: "SSX-VAULT-001",
    detail: "HAP-2026-DC-4471 · $8,250/MO · EFFECTIVE FEB 2026",
  },
  {
    id: "EVT-002",
    ts: "2026-01-15T09:00:00Z",
    type: "VAULT_CLOSED",
    dot: "emerald",
    label: "VAULT FULLY SUBSCRIBED",
    message: "ALL 6 LP SLOTS FILLED · VAULT CLOSED TO NEW CAPITAL",
    vault: "SSX-VAULT-001",
    detail: "$300,000 RAISE COMPLETE · OPERATIONAL STATUS ACHIEVED",
  },
  {
    id: "EVT-001",
    ts: "2026-01-10T08:00:00Z",
    type: "PLATFORM",
    dot: "emerald",
    label: "PLATFORM LAUNCH",
    message: "SSX SOVEREIGN TERMINAL · EXCHANGE LIVE",
    vault: null,
    detail: "REG D 506(c) · TACTICAL NOIR v2.0.0 · DCHA BACKSTOP ACTIVE",
  },
];

// ── Feed dot color → event type map ──────────────────────────
const EVENT_TYPE_LABELS = {
  LP_INTEREST: { dot: "amber", badge: "amber", label: "LP INTEREST" },
  HAP_CONFIRMED: { dot: "emerald", badge: "green", label: "HAP CONFIRMED" },
  LP_SLOT_FILLED: { dot: "emerald", badge: "green", label: "SLOT FILLED" },
  INSPECTION: { dot: "gold", badge: "gold", label: "INSPECTION" },
  HAP_APPLICATION: { dot: "amber", badge: "amber", label: "HAP PENDING" },
  DISTRIBUTION: { dot: "emerald", badge: "green", label: "DISTRIBUTION" },
  ACCREDITATION: { dot: "emerald", badge: "green", label: "ACCREDITATION" },
  TAX: { dot: "gold", badge: "gold", label: "TAX DOCUMENT" },
  OPERATING_AGREEMENT: { dot: "emerald", badge: "green", label: "LEGAL EXEC" },
  VAULT_CLOSED: { dot: "emerald", badge: "green", label: "VAULT CLOSED" },
  PLATFORM: { dot: "emerald", badge: "green", label: "SYSTEM" },
};

// ── Relative timestamp formatter ──────────────────────────────
const relativeTime = (isoString) => {
  const delta = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (delta < 60) return `${delta}S AGO`;
  if (delta < 3600) return `${Math.floor(delta / 60)}M AGO`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}H AGO`;
  return `${Math.floor(delta / 86400)}D AGO`;
};

// ─────────────────────────────────────────────────────────────
// ALLOCATION CLEARANCE MODAL
// The "Velvet Rope" gate. Opens when LP clicks REQUEST ALLOCATION CLEARANCE.
// Three-state machine: FORM → SUBMITTING → CONFIRMED
// ─────────────────────────────────────────────────────────────
const AllocationClearanceModal = ({ vault, onClose }) => {
  const { currentUser } = useSovereign();

  // Modal state machine: "FORM" | "SUBMITTING" | "CONFIRMED"
  const [stage, setStage] = useState("FORM");
  const [form, setForm] = useState({
    legalName: currentUser.legalName,
    entityType: currentUser.entityType,
    allocationAmt: "50000",
    accredType: "ACCREDITED INVESTOR",
    agreeTerms: false,
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const slotsRemaining = vault.lpCapacity - vault.lpsCommitted;

  const validate = () => {
    const errs = {};
    if (!form.legalName.trim()) errs.legalName = "REQUIRED";
    if (
      !form.allocationAmt ||
      isNaN(Number(form.allocationAmt)) ||
      Number(form.allocationAmt) < 50000
    )
      errs.allocationAmt = "MINIMUM $50,000";
    if (!form.agreeTerms) errs.agreeTerms = "MUST AGREE TO TERMS";
    return errs;
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setStage("SUBMITTING");
    setTimeout(() => setStage("CONFIRMED"), 2200);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          className="glass-modal corner-box"
          style={{
            width: "100%",
            maxWidth: "620px",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.99 }}
          transition={SPRING_HEAVY}
        >
          {/* ── Modal Header ── */}
          <div
            className="flex items-start justify-between p-6"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
          >
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <PhaseBadge>ALLOCATION CLEARANCE REQUEST</PhaseBadge>
                <StatusBadge type="green">REG D 506(c)</StatusBadge>
              </div>
              <div
                className="text-white font-black uppercase"
                style={{ fontSize: "16px", letterSpacing: "0.04em" }}
              >
                {vault.id} · {vault.shortId}
              </div>
              <div
                className="mono text-white/35 uppercase"
                style={{ fontSize: "9px", letterSpacing: "0.14em" }}
              >
                {vault.address} · {vault.city}
              </div>
            </div>
            <button
              onClick={onClose}
              className="mono text-white/25 hover:text-white/60 uppercase transition-colors"
              style={{ fontSize: "18px", lineHeight: 1, padding: "4px 8px" }}
            >
              ✕
            </button>
          </div>

          {/* ── Vault Snapshot ── */}
          <div
            className="grid grid-cols-3"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
          >
            {[
              {
                label: "LP MINIMUM",
                value: fmt.usd(vault.lpMinTicket),
                em: false,
              },
              {
                label: "PREFERRED RETURN",
                value: fmt.pct(vault.netYieldPct),
                em: true,
              },
              {
                label: "SLOTS REMAINING",
                value: `${slotsRemaining} OF ${vault.lpCapacity}`,
                em: slotsRemaining <= 2,
              },
            ].map((m, i) => (
              <div
                key={i}
                className="p-4"
                style={{
                  borderRight:
                    i < 2 ? "1px solid rgba(255,255,255,0.05)" : "none",
                }}
              >
                <Label>{m.label}</Label>
                <div
                  className={cn(
                    "mono font-black uppercase mt-1.5",
                    m.em ? "text-[var(--emerald)]" : "text-white"
                  )}
                  style={{ fontSize: "14px" }}
                >
                  {m.value}
                </div>
              </div>
            ))}
          </div>

          {/* ── State Machine Body ── */}
          <div className="p-6">
            <AnimatePresence mode="wait">
              {/* FORM STATE */}
              {stage === "FORM" && (
                <motion.div
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div>
                    <Label className="mb-2 block">INVESTOR INFORMATION</Label>
                    <div className="space-y-3">
                      {/* Legal Name */}
                      <div>
                        <div
                          className="mono text-white/30 uppercase mb-1.5"
                          style={{ fontSize: "8px", letterSpacing: "0.18em" }}
                        >
                          LEGAL ENTITY OR INDIVIDUAL NAME
                          {fieldErrors.legalName && (
                            <span
                              className="ml-2"
                              style={{ color: "var(--red-signal)" }}
                            >
                              · {fieldErrors.legalName}
                            </span>
                          )}
                        </div>
                        <input
                          className="terminal-input"
                          value={form.legalName}
                          onChange={(e) =>
                            handleChange("legalName", e.target.value)
                          }
                          placeholder="ENTER LEGAL NAME"
                          style={
                            fieldErrors.legalName
                              ? { borderColor: "rgba(255,68,102,0.45)" }
                              : {}
                          }
                        />
                      </div>

                      {/* Entity Type */}
                      <div>
                        <div
                          className="mono text-white/30 uppercase mb-1.5"
                          style={{ fontSize: "8px", letterSpacing: "0.18em" }}
                        >
                          ENTITY TYPE
                        </div>
                        <select
                          className="terminal-select"
                          value={form.entityType}
                          onChange={(e) =>
                            handleChange("entityType", e.target.value)
                          }
                        >
                          <option value="DELAWARE LIMITED PARTNERSHIP">
                            DELAWARE LIMITED PARTNERSHIP
                          </option>
                          <option value="DELAWARE LLC">DELAWARE LLC</option>
                          <option value="INDIVIDUAL ACCREDITED">
                            INDIVIDUAL ACCREDITED INVESTOR
                          </option>
                          <option value="FAMILY OFFICE">FAMILY OFFICE</option>
                          <option value="REGISTERED FUND">
                            REGISTERED INVESTMENT FUND
                          </option>
                          <option value="TRUST">IRREVOCABLE TRUST</option>
                        </select>
                      </div>

                      {/* Allocation Amount */}
                      <div>
                        <div
                          className="mono text-white/30 uppercase mb-1.5"
                          style={{ fontSize: "8px", letterSpacing: "0.18em" }}
                        >
                          REQUESTED ALLOCATION AMOUNT (USD) — MINIMUM $50,000
                          {fieldErrors.allocationAmt && (
                            <span
                              className="ml-2"
                              style={{ color: "var(--red-signal)" }}
                            >
                              · {fieldErrors.allocationAmt}
                            </span>
                          )}
                        </div>
                        <input
                          className="terminal-input"
                          type="number"
                          value={form.allocationAmt}
                          onChange={(e) =>
                            handleChange("allocationAmt", e.target.value)
                          }
                          placeholder="50000"
                          min="50000"
                          step="50000"
                          style={
                            fieldErrors.allocationAmt
                              ? { borderColor: "rgba(255,68,102,0.45)" }
                              : {}
                          }
                        />
                        {form.allocationAmt &&
                          !isNaN(Number(form.allocationAmt)) &&
                          Number(form.allocationAmt) >= 50000 && (
                            <div
                              className="mono text-white/30 uppercase mt-1"
                              style={{
                                fontSize: "9px",
                                letterSpacing: "0.12em",
                              }}
                            >
                              → ANNUAL PREFERRED RETURN:{" "}
                              <span
                                style={{
                                  color: "var(--emerald)",
                                  fontWeight: "bold",
                                }}
                              >
                                {fmt.usd(
                                  Number(form.allocationAmt) * vault.netYieldPct
                                )}
                                /YR
                              </span>
                            </div>
                          )}
                      </div>

                      {/* Accreditation Type */}
                      <div>
                        <div
                          className="mono text-white/30 uppercase mb-1.5"
                          style={{ fontSize: "8px", letterSpacing: "0.18em" }}
                        >
                          ACCREDITATION BASIS
                        </div>
                        <select
                          className="terminal-select"
                          value={form.accredType}
                          onChange={(e) =>
                            handleChange("accredType", e.target.value)
                          }
                        >
                          <option value="ACCREDITED INVESTOR">
                            ACCREDITED INVESTOR — INCOME / NET WORTH TEST
                          </option>
                          <option value="QUALIFIED PURCHASER">
                            QUALIFIED PURCHASER — $5M+ INVESTMENTS
                          </option>
                          <option value="QUALIFIED INSTITUTIONAL">
                            QUALIFIED INSTITUTIONAL BUYER (QIB)
                          </option>
                          <option value="CPA VERIFIED">
                            CPA / ATTORNEY VERIFIED ACCREDITATION
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Terms */}
                  <div
                    className="p-4"
                    style={{
                      background: "rgba(255,255,255,0.02)",
                      border: fieldErrors.agreeTerms
                        ? "1px solid rgba(255,68,102,0.35)"
                        : "1px solid rgba(255,255,255,0.06)",
                    }}
                  >
                    <label className="flex items-start gap-3 cursor-pointer">
                      <div
                        onClick={() =>
                          handleChange("agreeTerms", !form.agreeTerms)
                        }
                        className="shrink-0 mt-0.5 flex items-center justify-center transition-all duration-200"
                        style={{
                          width: "16px",
                          height: "16px",
                          border: `1px solid ${
                            form.agreeTerms
                              ? "rgba(0,255,136,0.5)"
                              : "rgba(255,255,255,0.15)"
                          }`,
                          background: form.agreeTerms
                            ? "rgba(0,255,136,0.12)"
                            : "transparent",
                          cursor: "pointer",
                        }}
                      >
                        {form.agreeTerms && (
                          <svg
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                          >
                            <path
                              d="M1 4L3.5 6.5L9 1"
                              stroke="var(--emerald)"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      <span
                        className="mono text-white/40 uppercase leading-relaxed"
                        style={{ fontSize: "8px", letterSpacing: "0.12em" }}
                      >
                        I CONFIRM I AM AN ACCREDITED INVESTOR UNDER SEC RULE
                        501(a) AND CONSENT TO RECEIVE THE PRIVATE PLACEMENT
                        MEMORANDUM (PPM). I UNDERSTAND THIS IS A SECURITIES
                        OFFERING UNDER REG D 506(c). SSX RESERVES THE RIGHT TO
                        DENY ANY ALLOCATION REQUEST PENDING COMPLIANCE REVIEW.
                        {fieldErrors.agreeTerms && (
                          <span
                            className="ml-2"
                            style={{ color: "var(--red-signal)" }}
                          >
                            — REQUIRED
                          </span>
                        )}
                      </span>
                    </label>
                  </div>

                  {/* Form Actions */}
                  <div className="flex items-center gap-3">
                    <button className="btn-submit" onClick={handleSubmit}>
                      <svg
                        width="12"
                        height="14"
                        viewBox="0 0 12 14"
                        fill="none"
                      >
                        <path
                          d="M6 1L10 5H7V9H5V5H2L6 1Z"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinejoin="round"
                        />
                        <rect
                          x="1"
                          y="11"
                          width="10"
                          height="2"
                          rx="0.5"
                          fill="currentColor"
                          opacity="0.5"
                        />
                      </svg>
                      SUBMIT CLEARANCE REQUEST
                    </button>
                    <button className="btn-cancel" onClick={onClose}>
                      CANCEL
                    </button>
                  </div>

                  {/* Reg D footnote */}
                  <div
                    className="mono text-white/20 uppercase"
                    style={{
                      fontSize: "8px",
                      letterSpacing: "0.12em",
                      lineHeight: "1.8",
                    }}
                  >
                    THIS IS NOT AN OFFER TO SELL SECURITIES. OFFERS ARE MADE
                    ONLY THROUGH DELIVERY OF A COMPLETE PPM. SECURITIES OFFERED
                    PURSUANT TO REGULATION D, RULE 506(c). ACCREDITED INVESTORS
                    ONLY.
                  </div>
                </motion.div>
              )}

              {/* SUBMITTING STATE */}
              {stage === "SUBMITTING" && (
                <motion.div
                  key="submitting"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center py-12 space-y-5"
                >
                  {/* Animated compliance scanner */}
                  <div
                    className="relative"
                    style={{ width: "64px", height: "64px" }}
                  >
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{ border: "1px solid var(--emerald-border)" }}
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 2,
                        ease: "linear",
                        repeat: Infinity,
                      }}
                    />
                    <motion.div
                      className="absolute"
                      style={{
                        inset: "6px",
                        border: "1px solid rgba(0,255,136,0.3)",
                        borderTopColor: "var(--emerald)",
                        borderRadius: "50%",
                      }}
                      animate={{ rotate: -360 }}
                      transition={{
                        duration: 1.4,
                        ease: "linear",
                        repeat: Infinity,
                      }}
                    />
                    <div
                      className="absolute inset-0 flex items-center justify-center"
                      style={{ fontSize: "9px", color: "var(--emerald)" }}
                    >
                      <svg
                        width="18"
                        height="20"
                        viewBox="0 0 18 20"
                        fill="none"
                      >
                        <rect
                          x="1"
                          y="7"
                          width="16"
                          height="12"
                          rx="1"
                          stroke="currentColor"
                          strokeWidth="1.2"
                        />
                        <path
                          d="M5 7V5C5 2.79 6.79 1 9 1C11.21 1 13 2.79 13 5V7"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                        />
                        <circle
                          cx="9"
                          cy="13"
                          r="2"
                          fill="currentColor"
                          opacity="0.7"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="space-y-2 text-center">
                    <div
                      className="mono font-black uppercase"
                      style={{
                        fontSize: "11px",
                        color: "var(--emerald)",
                        letterSpacing: "0.2em",
                      }}
                    >
                      COMPLIANCE REVIEW IN PROGRESS
                    </div>
                    <div
                      className="mono text-white/35 uppercase"
                      style={{ fontSize: "9px", letterSpacing: "0.14em" }}
                    >
                      RUNNING ACCREDITATION VERIFICATION
                      <br />
                      WATCHLIST SCREEN · AML CLEARANCE PROTOCOL
                    </div>
                  </div>
                  {/* Progress dots */}
                  <div className="flex items-center gap-2">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <motion.div
                        key={i}
                        className="glow-dot"
                        style={{ width: "5px", height: "5px" }}
                        animate={{ opacity: [0.2, 1, 0.2] }}
                        transition={{
                          duration: 1.2,
                          repeat: Infinity,
                          delay: i * 0.22,
                        }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* CONFIRMED STATE */}
              {stage === "CONFIRMED" && (
                <motion.div
                  key="confirmed"
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={SPRING_HEAVY}
                  className="py-8 space-y-5"
                >
                  {/* Success Header */}
                  <div className="text-center space-y-3">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ ...SPRING_HEAVY, delay: 0.1 }}
                      className="flex justify-center"
                    >
                      <div
                        style={{
                          width: "52px",
                          height: "52px",
                          border: "1px solid rgba(0,255,136,0.4)",
                          background: "rgba(0,255,136,0.06)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 0 24px rgba(0,255,136,0.12)",
                        }}
                      >
                        <svg
                          width="22"
                          height="18"
                          viewBox="0 0 22 18"
                          fill="none"
                        >
                          <motion.path
                            d="M1 9L8 16L21 1"
                            stroke="var(--emerald)"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{
                              duration: 0.6,
                              delay: 0.2,
                              ease: "easeOut",
                            }}
                          />
                        </svg>
                      </div>
                    </motion.div>
                    <div
                      className="mono font-black uppercase"
                      style={{
                        fontSize: "14px",
                        color: "var(--emerald)",
                        letterSpacing: "0.2em",
                      }}
                    >
                      CLEARANCE REQUEST SUBMITTED
                    </div>
                    <div
                      className="mono text-white/40 uppercase"
                      style={{
                        fontSize: "9px",
                        letterSpacing: "0.14em",
                        lineHeight: "1.8",
                      }}
                    >
                      YOUR INTEREST HAS BEEN LOGGED IN THE SOVEREIGN LEDGER.
                      <br />
                      SSX COMPLIANCE WILL CONTACT YOU WITHIN 1–2 BUSINESS DAYS.
                    </div>
                  </div>

                  {/* Confirmation Details */}
                  <CornerBox className="glass-dark overflow-hidden">
                    {[
                      {
                        label: "REQUEST ID",
                        value: `ALLOC-2026-${Math.random()
                          .toString(36)
                          .slice(2, 8)
                          .toUpperCase()}`,
                      },
                      {
                        label: "VAULT",
                        value: `${vault.id} · ${vault.address}`,
                      },
                      {
                        label: "REQUESTED AMOUNT",
                        value: fmt.usd(Number(form.allocationAmt)),
                        em: true,
                      },
                      {
                        label: "PROJECTED ANNUAL RETURN",
                        value: fmt.usd(
                          Number(form.allocationAmt) * vault.netYieldPct
                        ),
                        em: true,
                      },
                      {
                        label: "NEXT STEP",
                        value: "PPM DELIVERY · COMPLIANCE REVIEW",
                      },
                    ].map((row, i) => (
                      <LedgerRow
                        key={i}
                        label={row.label}
                        value={row.value}
                        highlight={row.em}
                      />
                    ))}
                  </CornerBox>

                  <div className="flex justify-center">
                    <button className="btn-submit" onClick={onClose}>
                      CLOSE TERMINAL
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─────────────────────────────────────────────────────────────
// SLOT FILL VISUALIZER
// Visual pip grid showing filled vs open LP slots at a glance.
// ─────────────────────────────────────────────────────────────
const SlotPips = ({ filled, total, size = "md" }) => {
  const dim =
    size === "sm" ? { pip: "10px", gap: "3px" } : { pip: "13px", gap: "4px" };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: dim.gap,
        flexWrap: "wrap",
      }}
    >
      {Array.from({ length: total }).map((_, i) => {
        const isFilled = i < filled;
        const isLast = isFilled && i === total - 1;
        return (
          <motion.div
            key={i}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ ...SPRING_HEAVY, delay: 0.35 + i * 0.055 }}
            style={{
              width: dim.pip,
              height: dim.pip,
              border: `1px solid ${
                isFilled
                  ? i >= total - 2 && filled < total
                    ? "rgba(245,158,11,0.7)"
                    : "rgba(0,255,136,0.6)"
                  : "rgba(255,255,255,0.12)"
              }`,
              background: isFilled
                ? i >= total - 2 && filled < total
                  ? "rgba(245,158,11,0.25)"
                  : "rgba(0,255,136,0.25)"
                : "rgba(255,255,255,0.03)",
              boxShadow: isFilled
                ? i >= total - 2 && filled < total
                  ? "0 0 6px rgba(245,158,11,0.3)"
                  : "0 0 6px rgba(0,255,136,0.2)"
                : "none",
              borderRadius: "1px",
              flexShrink: 0,
            }}
          />
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// VAULT ALLOCATION CARD (Exchange Version)
// ─────────────────────────────────────────────────────────────
const ExchangeVaultCard = ({ vault, index, onRequestAllocation }) => {
  const slotsRemaining = vault.lpCapacity - vault.lpsCommitted;
  const fillPct = vault.lpsCommitted / vault.lpCapacity;
  const isFullySubscribed = slotsRemaining === 0;
  const isCriticallyLow = slotsRemaining > 0 && slotsRemaining <= 2;

  const phaseConfig = {
    OPERATIONAL: {
      badge: "green",
      label: "ACTIVE · YIELD LIVE",
      headerAccent: "var(--emerald)",
    },
    FUNDING: {
      badge: "amber",
      label: "FUNDING · OPEN",
      headerAccent: "var(--amber-signal)",
    },
    PIPELINE: {
      badge: "muted",
      label: "PIPELINE · REVIEW",
      headerAccent: "rgba(255,255,255,0.2)",
    },
  };
  const pc = phaseConfig[vault.phase] || phaseConfig.PIPELINE;

  return (
    <motion.div variants={STAGGER_CHILD}>
      <CornerBox
        className={cn(
          "glass-dark overflow-hidden card-hover",
          vault.phase === "OPERATIONAL" ? "glass-emerald" : ""
        )}
      >
        {/* ── Card Header ── */}
        <div
          className="flex items-start justify-between p-5"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              {/* LIVE / REVIEW Badge */}
              <span
                className="mono font-black live-flicker"
                style={{
                  fontSize: "9px",
                  letterSpacing: "0.22em",
                  color: pc.headerAccent,
                  background: `${pc.headerAccent}10`,
                  border: `1px solid ${pc.headerAccent}30`,
                  padding: "2px 8px",
                }}
              >
                {vault.phase === "PIPELINE" ? "● REVIEW" : "● LIVE"}
              </span>
              <StatusBadge type={pc.badge}>{pc.label}</StatusBadge>
              {vault.phase === "OPERATIONAL" && (
                <VerifiedBadge label="HAP ACTIVE" />
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Short ID Medallion */}
              <div
                className="flex items-center justify-center mono font-black"
                style={{
                  width: "40px",
                  height: "40px",
                  border: "1px solid rgba(0,255,136,0.2)",
                  background: "rgba(0,255,136,0.05)",
                  fontSize: "13px",
                  color: "var(--emerald)",
                  letterSpacing: "0.04em",
                  flexShrink: 0,
                }}
              >
                {vault.shortId}
              </div>
              <div>
                <div
                  className="text-white font-black uppercase"
                  style={{ fontSize: "15px", letterSpacing: "0.04em" }}
                >
                  {vault.address}
                </div>
                <div
                  className="mono text-white/35 uppercase"
                  style={{ fontSize: "9px", letterSpacing: "0.12em" }}
                >
                  {vault.city} · {vault.bedrooms}BR /{" "}
                  {vault.sqft.toLocaleString()} SQFT
                </div>
              </div>
            </div>
          </div>

          {/* Right: Raise target */}
          <div className="text-right shrink-0 ml-4">
            <div
              className="mono font-black"
              style={{
                fontSize: "20px",
                color: "var(--emerald)",
                letterSpacing: "-0.02em",
              }}
            >
              {fmt.usd(vault.raise)}
            </div>
            <div
              className="mono text-white/25 uppercase"
              style={{ fontSize: "8px", letterSpacing: "0.14em" }}
            >
              TOTAL RAISE
            </div>
          </div>
        </div>

        {/* ── 3-Column Sub-Grid: GAV / HAP / YIELD ── */}
        <div
          className="grid grid-cols-3"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
        >
          {[
            {
              label: "GROSS ASSET VALUE",
              value: fmt.usd(vault.gav),
              sub: "PROPERTY GAV",
              em: false,
            },
            {
              label: "HAP RATE",
              value: `${fmt.usd(vault.monthlyHAP)}/MO`,
              sub: "DCHA · HUD BACKSTOP",
              em: true,
            },
            {
              label: "LP NET YIELD",
              value: fmt.pct(vault.netYieldPct),
              sub: "PREFERRED RETURN",
              em: true,
            },
          ].map((col, ci) => (
            <div
              key={ci}
              className="p-4 text-center"
              style={{
                borderRight:
                  ci < 2 ? "1px solid rgba(255,255,255,0.04)" : "none",
              }}
            >
              <Label className="justify-center">{col.label}</Label>
              <div
                className={cn(
                  "mono font-black uppercase mt-2",
                  col.em ? "text-[var(--emerald)]" : "text-white"
                )}
                style={{ fontSize: "14px", letterSpacing: "0.02em" }}
              >
                {col.value}
              </div>
              <div
                className="mono text-white/25 uppercase mt-0.5"
                style={{ fontSize: "8px", letterSpacing: "0.1em" }}
              >
                {col.sub}
              </div>
            </div>
          ))}
        </div>

        {/* ── Slot Fill Visualizer ── */}
        <div
          className="px-5 py-4"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
        >
          {/* Header row */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Label>LP ALLOCATION SLOTS</Label>
              {isCriticallyLow && (
                <motion.span
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 1.4, repeat: Infinity }}
                  className="mono font-black uppercase"
                  style={{
                    fontSize: "9px",
                    color: "var(--red-signal)",
                    background: "rgba(255,68,102,0.08)",
                    border: "1px solid rgba(255,68,102,0.2)",
                    padding: "2px 8px",
                    letterSpacing: "0.16em",
                  }}
                >
                  ⚠ {slotsRemaining} SLOT{slotsRemaining > 1 ? "S" : ""}{" "}
                  REMAINING
                </motion.span>
              )}
              {isFullySubscribed && (
                <span
                  className="mono font-black uppercase"
                  style={{
                    fontSize: "9px",
                    color: "rgba(255,255,255,0.35)",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    padding: "2px 8px",
                    letterSpacing: "0.16em",
                  }}
                >
                  FULLY SUBSCRIBED
                </span>
              )}
            </div>
            <span
              className="mono font-black"
              style={{
                fontSize: "12px",
                color: isFullySubscribed
                  ? "rgba(255,255,255,0.4)"
                  : "var(--emerald)",
              }}
            >
              {vault.lpsCommitted} / {vault.lpCapacity}
            </span>
          </div>

          {/* Pip row */}
          <div className="flex items-center gap-4">
            <SlotPips filled={vault.lpsCommitted} total={vault.lpCapacity} />

            {/* Fill % bar */}
            <div className="flex-1">
              <div
                className="relative h-1 rounded-full overflow-hidden"
                style={{ background: "rgba(255,255,255,0.06)" }}
              >
                <motion.div
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{
                    background: isFullySubscribed
                      ? "linear-gradient(90deg, rgba(255,255,255,0.25), rgba(255,255,255,0.15))"
                      : isCriticallyLow
                      ? "linear-gradient(90deg, var(--amber-signal), rgba(245,158,11,0.6))"
                      : "linear-gradient(90deg, var(--emerald), rgba(0,255,136,0.5))",
                    boxShadow: isFullySubscribed
                      ? "none"
                      : isCriticallyLow
                      ? "0 0 8px var(--amber-signal)"
                      : "0 0 8px var(--emerald)",
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: `${fillPct * 100}%` }}
                  transition={{ ...SPRING_HEAVY, delay: 0.3 + index * 0.1 }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span
                  className="mono text-white/20 uppercase"
                  style={{ fontSize: "8px" }}
                >
                  0%
                </span>
                <span
                  className="mono font-black uppercase"
                  style={{
                    fontSize: "9px",
                    color: isFullySubscribed
                      ? "rgba(255,255,255,0.3)"
                      : isCriticallyLow
                      ? "var(--amber-signal)"
                      : "var(--emerald)",
                  }}
                >
                  {Math.round(fillPct * 100)}% FILLED
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── HAP Contract Footer ── */}
        <div
          className="px-5 py-3 flex items-center justify-between"
          style={{
            borderBottom: "1px solid rgba(255,255,255,0.04)",
            background:
              vault.phase === "OPERATIONAL"
                ? "rgba(0,255,136,0.02)"
                : "transparent",
          }}
        >
          <div className="flex items-center gap-2">
            <Dot
              color={
                vault.phase === "OPERATIONAL"
                  ? "emerald"
                  : vault.phase === "FUNDING"
                  ? "amber"
                  : "muted"
              }
              pulse={vault.phase === "OPERATIONAL"}
            />
            <span
              className="mono text-white/25 uppercase"
              style={{ fontSize: "9px", letterSpacing: "0.12em" }}
            >
              HAP: {vault.hapContractNumber}
            </span>
          </div>
          <span
            className="mono font-black uppercase"
            style={{
              fontSize: "9px",
              letterSpacing: "0.12em",
              color:
                vault.phase === "OPERATIONAL"
                  ? "var(--emerald)"
                  : "rgba(255,255,255,0.25)",
            }}
          >
            {vault.hapBackstop}
          </span>
        </div>

        {/* ── Dynamic CTA Button ── */}
        <div className="p-4">
          {isFullySubscribed ? (
            // Fully subscribed — queue button
            <button
              className="btn-queue"
              onClick={() => onRequestAllocation(vault, true)}
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                <circle
                  cx="5.5"
                  cy="5.5"
                  r="4.5"
                  stroke="currentColor"
                  strokeWidth="1"
                />
                <path
                  d="M5.5 3V5.5L7 7"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
              </svg>
              FULLY SUBSCRIBED — JOIN WAITLIST
            </button>
          ) : vault.phase === "PIPELINE" ? (
            // Pipeline — not yet open
            <button className="btn-subscribed" disabled>
              <svg width="11" height="13" viewBox="0 0 11 13" fill="none">
                <rect
                  x="0.5"
                  y="5"
                  width="10"
                  height="7.5"
                  rx="0.8"
                  stroke="currentColor"
                  strokeWidth="1"
                />
                <path
                  d="M3 5V3.5C3 1.9 4.12 1 5.5 1C6.88 1 8 1.9 8 3.5V5"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
              </svg>
              PIPELINE STAGE — NOT YET OPEN
            </button>
          ) : (
            // Open slots — primary CTA
            <button
              className="btn-allocate"
              onClick={() => onRequestAllocation(vault, false)}
            >
              <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
                <path
                  d="M6 1V9M3 6L6 9L9 6"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <rect
                  x="1"
                  y="11"
                  width="10"
                  height="1.5"
                  rx="0.5"
                  fill="currentColor"
                  opacity="0.5"
                />
              </svg>
              REQUEST ALLOCATION CLEARANCE
              {isCriticallyLow && (
                <span
                  className="mono font-black"
                  style={{
                    marginLeft: "4px",
                    color: "var(--amber-signal)",
                    fontSize: "8px",
                    letterSpacing: "0.12em",
                  }}
                >
                  · {slotsRemaining} LEFT
                </span>
              )}
            </button>
          )}
        </div>
      </CornerBox>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────
// EXCHANGE VIEW ROOT
// ─────────────────────────────────────────────────────────────
const ExchangeView = () => {
  const { activeVaults, systemMetrics, V2_CONSTANTS } = useSovereign();
  const [modalVault, setModalVault] = useState(null);
  const [isQueueMode, setIsQueueMode] = useState(false);
  const [feedLimit, setFeedLimit] = useState(8);

  const handleRequestAllocation = (vault, queueMode = false) => {
    setModalVault(vault);
    setIsQueueMode(queueMode);
  };
  const handleCloseModal = () => {
    setModalVault(null);
    setIsQueueMode(false);
  };

  // Derived Exchange metrics
  const avgFillRate = systemMetrics.avgFillRate;
  const liveVaults = activeVaults.filter((v) => v.phase !== "PIPELINE").length;
  const totalOpenSlots = activeVaults.reduce(
    (s, v) => s + Math.max(0, v.lpCapacity - v.lpsCommitted),
    0
  );

  return (
    <>
      <ViewShell viewKey="EXCHANGE">
        {/* ── Section Header ── */}
        <motion.div
          className="mb-6 space-y-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={STIFF_INSTITUTIONAL}
        >
          <div className="flex items-center gap-3 flex-wrap">
            <PhaseBadge>SSX LIVE EXCHANGE // SOVEREIGN VAULT MARKET</PhaseBadge>
            <StatusBadge type="green">EXCHANGE LIVE</StatusBadge>
            <VerifiedBadge label="REG D 506(c)" />
          </div>
          <h1
            className="text-white font-black uppercase leading-tight"
            style={{ fontSize: "26px", letterSpacing: "0.02em" }}
          >
            SSX LIVE EXCHANGE
            <br />
            <span style={{ color: "var(--emerald)", fontSize: "17px" }}>
              INSTITUTIONAL VAULT ALLOCATION BOARD · ACCREDITED INVESTORS ONLY
            </span>
          </h1>
          <p
            className="mono text-white/25 uppercase"
            style={{ fontSize: "9px", letterSpacing: "0.2em" }}
          >
            DCHA / HUD HAP BACKSTOPPED · DELAWARE SERIES LLC STRUCTURE · $50,000
            MINIMUM · 6 LPs / VAULT · SPOT ALLOCATIONS CLOSING FAST
          </p>
        </motion.div>

        {/* ── Top Metrics Grid ── */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
          variants={STAGGER_CONTAINER}
          initial="hidden"
          animate="visible"
        >
          {[
            {
              label: "VAULTS LISTED",
              value: `${activeVaults.length} LIVE`,
              sub: `${systemMetrics.operationalVaults} OPERATIONAL · ${systemMetrics.fundingVaults} FUNDING · ${systemMetrics.pipelineVaults} PIPELINE`,
              highlight: false,
              trend: "up",
            },
            {
              label: "LP RAISE / VAULT",
              value: `${fmt.usd(V2_CONSTANTS.RAISE_PER_VAULT)}`,
              sub: `6 LPs × ${fmt.usd(V2_CONSTANTS.LP_MIN_TICKET)} MIN`,
              highlight: false,
            },
            {
              label: "AVG VAULT FILL RATE",
              value: fmt.pct(avgFillRate),
              sub: `${totalOpenSlots} OPEN SLOT${
                totalOpenSlots !== 1 ? "S" : ""
              } REMAINING`,
              highlight: totalOpenSlots <= 4,
              trend: avgFillRate > 0.6 ? "up" : undefined,
            },
            {
              label: "LP NET PREFERRED RETURN",
              value: fmt.pct(V2_CONSTANTS.NET_YIELD_PCT),
              sub: "HUD BACKSTOPPED HAP CASHFLOW",
              highlight: true,
            },
          ].map((card, i) => (
            <motion.div key={i} variants={STAGGER_CHILD}>
              <MetricCard {...card} />
            </motion.div>
          ))}
        </motion.div>

        {/* ── Scarcity Banner ── */}
        {totalOpenSlots <= 4 && totalOpenSlots > 0 && (
          <motion.div
            className="mb-5"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...STIFF_INSTITUTIONAL, delay: 0.2 }}
          >
            <div
              className="flex items-center justify-between px-5 py-3 corner-box"
              style={{
                background: "rgba(255,68,102,0.04)",
                border: "1px solid rgba(255,68,102,0.2)",
              }}
            >
              <div className="flex items-center gap-3">
                <motion.div
                  className="glow-dot-red scarcity-glow"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity }}
                />
                <span
                  className="mono font-black uppercase"
                  style={{
                    fontSize: "10px",
                    color: "var(--red-signal)",
                    letterSpacing: "0.18em",
                  }}
                >
                  SCARCITY ALERT · ONLY {totalOpenSlots} LP SLOT
                  {totalOpenSlots > 1 ? "S" : ""} REMAINING ACROSS ALL OPEN
                  VAULTS
                </span>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge type="red">HIGH DEMAND</StatusBadge>
                <span
                  className="mono text-white/25 uppercase"
                  style={{ fontSize: "8px", letterSpacing: "0.12em" }}
                >
                  FIRST-CLEARED · FIRST-ALLOCATED
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Section Label ── */}
        <motion.div
          className="flex items-center justify-between mb-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.28 }}
        >
          <div className="flex items-center gap-3">
            <Label>LIVE VAULT ALLOCATION BOARD</Label>
            <span
              className="mono uppercase live-flicker"
              style={{
                fontSize: "9px",
                color: "var(--emerald)",
                background: "rgba(0,255,136,0.07)",
                border: "1px solid rgba(0,255,136,0.15)",
                padding: "2px 8px",
                letterSpacing: "0.16em",
              }}
            >
              ● {liveVaults} VAULT{liveVaults !== 1 ? "S" : ""} TRADING
            </span>
          </div>
          <span
            className="mono text-white/20 uppercase"
            style={{ fontSize: "8px", letterSpacing: "0.12em" }}
          >
            SORTED · OPERATIONAL FIRST
          </span>
        </motion.div>

        {/* ── Vault Cards Grid ── */}
        <motion.div
          className="space-y-4 mb-8"
          variants={STAGGER_CONTAINER}
          initial="hidden"
          animate="visible"
        >
          {[...activeVaults]
            .sort((a, b) => {
              const order = { OPERATIONAL: 0, FUNDING: 1, PIPELINE: 2 };
              return (order[a.phase] ?? 9) - (order[b.phase] ?? 9);
            })
            .map((vault, i) => (
              <ExchangeVaultCard
                key={vault.id}
                vault={vault}
                index={i}
                onRequestAllocation={handleRequestAllocation}
              />
            ))}
        </motion.div>

        {/* ── Platform Activity Feed ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...SPRING_HEAVY, delay: 0.5 }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Label>PLATFORM ACTIVITY FEED — LIVE TELEMETRY</Label>
              <span
                className="mono uppercase live-flicker"
                style={{
                  fontSize: "8px",
                  color: "var(--emerald)",
                  background: "rgba(0,255,136,0.07)",
                  border: "1px solid rgba(0,255,136,0.12)",
                  padding: "2px 8px",
                  letterSpacing: "0.14em",
                }}
              >
                ● STREAMING
              </span>
            </div>
            <span
              className="mono text-white/20 uppercase"
              style={{ fontSize: "8px", letterSpacing: "0.12em" }}
            >
              {ACTIVITY_FEED.length} EVENTS LOGGED
            </span>
          </div>

          <CornerBox className="glass-dark overflow-hidden">
            <div
              className="flex items-center justify-between px-5 py-3"
              style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
            >
              {/* Legend */}
              <div className="flex items-center gap-5 flex-wrap">
                {[
                  { color: "emerald", label: "COMPLETED / CONFIRMED" },
                  { color: "amber", label: "PENDING / IN PROGRESS" },
                  { color: "gold", label: "SCHEDULED / COMPLIANCE" },
                ].map((leg, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Dot color={leg.color} />
                    <span
                      className="mono text-white/30 uppercase"
                      style={{ fontSize: "8px", letterSpacing: "0.1em" }}
                    >
                      {leg.label}
                    </span>
                  </div>
                ))}
              </div>
              <span
                className="mono text-white/20 uppercase"
                style={{ fontSize: "8px", letterSpacing: "0.1em" }}
              >
                SHOWING {Math.min(feedLimit, ACTIVITY_FEED.length)} OF{" "}
                {ACTIVITY_FEED.length}
              </span>
            </div>

            {/* Feed Entries */}
            {ACTIVITY_FEED.slice(0, feedLimit).map((event, ei) => {
              const typeConfig = EVENT_TYPE_LABELS[event.type] || {
                dot: "muted",
                badge: "muted",
                label: event.type,
              };
              return (
                <motion.div
                  key={event.id}
                  className="feed-entry"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    ...STIFF_INSTITUTIONAL,
                    delay: 0.55 + ei * 0.045,
                  }}
                >
                  {/* Left: Dot */}
                  <div className="shrink-0 mt-0.5">
                    <Dot
                      color={typeConfig.dot}
                      pulse={typeConfig.dot === "emerald" && ei === 0}
                    />
                  </div>

                  {/* Center: Event Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="mono font-black uppercase"
                        style={{
                          fontSize: "10px",
                          letterSpacing: "0.1em",
                          color:
                            typeConfig.dot === "emerald"
                              ? "rgba(255,255,255,0.85)"
                              : typeConfig.dot === "amber"
                              ? "var(--amber-signal)"
                              : "var(--gold-signal)",
                        }}
                      >
                        {event.label}
                      </span>
                      <StatusBadge type={typeConfig.badge}>
                        {typeConfig.label}
                      </StatusBadge>
                      {event.vault && (
                        <span
                          className="mono text-white/30 uppercase"
                          style={{ fontSize: "8px", letterSpacing: "0.12em" }}
                        >
                          {event.vault}
                        </span>
                      )}
                    </div>
                    <div
                      className="mono text-white/45 uppercase mt-0.5"
                      style={{ fontSize: "9px", letterSpacing: "0.1em" }}
                    >
                      {event.message}
                    </div>
                    {event.detail && (
                      <div
                        className="mono text-white/22 uppercase mt-0.5"
                        style={{ fontSize: "8px", letterSpacing: "0.1em" }}
                      >
                        {event.detail}
                      </div>
                    )}
                  </div>

                  {/* Right: Timestamp */}
                  <div className="shrink-0 text-right">
                    <div
                      className="mono text-white/20 uppercase"
                      style={{ fontSize: "8px", letterSpacing: "0.1em" }}
                    >
                      {relativeTime(event.ts)}
                    </div>
                    <div
                      className="mono text-white/15 uppercase mt-0.5"
                      style={{ fontSize: "7px", letterSpacing: "0.08em" }}
                    >
                      {event.id}
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Load More / Footer */}
            <div
              className="flex items-center justify-between px-5 py-3"
              style={{
                borderTop: "1px solid rgba(255,255,255,0.05)",
                background: "rgba(0,255,136,0.02)",
              }}
            >
              {feedLimit < ACTIVITY_FEED.length ? (
                <button
                  className="mono text-white/30 hover:text-white/60 uppercase transition-colors"
                  style={{
                    fontSize: "9px",
                    letterSpacing: "0.16em",
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                  }}
                  onClick={() => setFeedLimit((l) => l + 6)}
                >
                  + LOAD {Math.min(6, ACTIVITY_FEED.length - feedLimit)} MORE
                  EVENTS
                </button>
              ) : (
                <span
                  className="mono text-white/20 uppercase"
                  style={{ fontSize: "8px", letterSpacing: "0.12em" }}
                >
                  ALL EVENTS LOADED · {ACTIVITY_FEED.length} TOTAL
                </span>
              )}
              <div className="flex items-center gap-2">
                <Dot color="emerald" pulse />
                <span
                  className="mono text-white/25 uppercase"
                  style={{ fontSize: "8px", letterSpacing: "0.12em" }}
                >
                  FEED LIVE · SSX ORACLE v2.0
                </span>
              </div>
            </div>
          </CornerBox>
        </motion.div>

        {/* ── Bottom Reg D Footer ── */}
        <motion.div
          className="mt-5 verdict-box"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <div
            className="text-center mono text-white/30 uppercase"
            style={{
              fontSize: "9px",
              letterSpacing: "0.16em",
              lineHeight: "1.9",
            }}
          >
            THIS EXCHANGE BOARD CONSTITUTES AN ADVERTISEMENT UNDER SEC RULE
            506(c). ALL LISTED VAULTS ARE AVAILABLE EXCLUSIVELY TO VERIFIED
            ACCREDITED INVESTORS AS DEFINED UNDER SEC RULE 501(a).
            <br />
            <span style={{ color: "var(--emerald)", fontWeight: "bold" }}>
              SSX HOLDINGS LLC · DELAWARE SERIES LLC · REG D 506(c) · DCHA / HUD
              HAP BACKSTOP · FY2026
            </span>
          </div>
        </motion.div>
      </ViewShell>

      {/* ── Allocation Clearance Modal Portal ── */}
      <AnimatePresence>
        {modalVault && (
          <AllocationClearanceModal
            key={modalVault.id}
            vault={modalVault}
            queueMode={isQueueMode}
            onClose={handleCloseModal}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// § 7. PORTFOLIO VIEW (Prompt 2 — Preserved verbatim)
// ─────────────────────────────────────────────────────────────
const ACH_LEDGER = [
  {
    date: "2026-04-01",
    vault: "SSX-VAULT-001",
    description: "Q1 2026 PREFERRED RETURN",
    amount: 1_562.5,
    status: "CLEARED",
    method: "ACH · WIRE",
    ref: "ACH-2026-Q1-001",
  },
  {
    date: "2026-03-01",
    vault: "SSX-VAULT-001",
    description: "FEB 2026 HAP DISTRIBUTION",
    amount: 520.83,
    status: "CLEARED",
    method: "ACH · WIRE",
    ref: "ACH-2026-FEB-001",
  },
  {
    date: "2026-02-01",
    vault: "SSX-VAULT-001",
    description: "JAN 2026 HAP DISTRIBUTION",
    amount: 520.83,
    status: "CLEARED",
    method: "ACH · WIRE",
    ref: "ACH-2026-JAN-001",
  },
  {
    date: "2026-01-15",
    vault: "SSX-VAULT-001",
    description: "ORIGINATION / CLOSING MEMO",
    amount: 0,
    status: "RECORD ONLY",
    method: "N/A",
    ref: "CLOSE-2026-001",
  },
];
const YTD_DISTRIBUTIONS = ACH_LEDGER.filter(
  (r) => r.status === "CLEARED"
).reduce((s, r) => s + r.amount, 0);

const VaultAllocationCard = ({ vault, userPosition, index }) => {
  const monthlyIncome =
    (userPosition.committed * V2_CONSTANTS.NET_YIELD_PCT) / 12;
  const annualReturn = userPosition.committed * V2_CONSTANTS.NET_YIELD_PCT;
  const fillPct = vault.capitalDeployed / vault.capitalTarget;
  const phaseConfig = {
    OPERATIONAL: { badge: "green", label: "OPERATIONAL · YIELD ACTIVE" },
    FUNDING: { badge: "amber", label: "FUNDING · PRE-YIELD" },
    PIPELINE: { badge: "muted", label: "PIPELINE" },
  };
  const pc = phaseConfig[vault.phase] || phaseConfig.PIPELINE;
  return (
    <motion.div variants={STAGGER_CHILD}>
      <CornerBox
        className={cn(
          "glass-dark card-hover overflow-hidden",
          vault.phase === "OPERATIONAL" ? "glass-emerald" : ""
        )}
      >
        <div
          className="flex items-start justify-between p-5"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Label>{vault.id}</Label>
              <StatusBadge type={pc.badge}>{pc.label}</StatusBadge>
              {vault.phase === "OPERATIONAL" && (
                <VerifiedBadge label="HUD BACKSTOPPED" />
              )}
            </div>
            <div
              className="text-white font-black uppercase"
              style={{ fontSize: "15px", letterSpacing: "0.04em" }}
            >
              {vault.address}
            </div>
            <div
              className="mono text-white/35 uppercase"
              style={{ fontSize: "9px", letterSpacing: "0.14em" }}
            >
              {vault.city} · {vault.assetType} · {vault.bedrooms}BR /{" "}
              {vault.sqft.toLocaleString()} SQFT
            </div>
          </div>
          <div className="text-right space-y-0.5 shrink-0 ml-4">
            <div
              className="mono font-black"
              style={{
                fontSize: "22px",
                color: "var(--emerald)",
                letterSpacing: "-0.02em",
              }}
            >
              {fmt.usd(userPosition.committed)}
            </div>
            <div
              className="mono text-white/30 uppercase"
              style={{ fontSize: "9px", letterSpacing: "0.12em" }}
            >
              YOUR ALLOCATION
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4">
          {[
            {
              label: "MONTHLY INCOME",
              value: fmt.usd(monthlyIncome),
              em: vault.phase === "OPERATIONAL",
              sub: "12.50% PREF · MONTHLY",
            },
            {
              label: "ANNUAL RETURN",
              value: fmt.usd(annualReturn),
              em: false,
              sub: "PREFERRED RETURN",
            },
            {
              label: "HAP GROSS REVENUE",
              value: `${fmt.usd(vault.grossRevenueAnnual)}/YR`,
              em: false,
              sub: "DCHA BACKSTOPPED",
            },
            {
              label: "VAULT FILL",
              value: `${Math.round(fillPct * 100)}%`,
              em: fillPct === 1,
              sub: `${vault.lpsCommitted}/${vault.lpCapacity} LPs`,
            },
          ].map((m, mi) => (
            <div
              key={mi}
              className="p-4"
              style={{
                borderRight:
                  mi < 3 ? "1px solid rgba(255,255,255,0.04)" : "none",
              }}
            >
              <Label>{m.label}</Label>
              <div
                className={cn(
                  "mono font-black uppercase mt-1.5",
                  m.em ? "text-[var(--emerald)]" : "text-white"
                )}
                style={{ fontSize: "13px" }}
              >
                {m.value}
              </div>
              {m.sub && (
                <div
                  className="mono text-white/25 uppercase mt-0.5"
                  style={{ fontSize: "8px", letterSpacing: "0.12em" }}
                >
                  {m.sub}
                </div>
              )}
            </div>
          ))}
        </div>
        <div
          className="px-5 pb-4 pt-3"
          style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}
        >
          <div className="flex justify-between items-center mb-2">
            <Label>VAULT CAPITAL DEPLOYMENT</Label>
            <span
              className="mono text-white/35 uppercase"
              style={{ fontSize: "9px", letterSpacing: "0.12em" }}
            >
              {fmt.usd(vault.capitalDeployed)} / {fmt.usd(vault.capitalTarget)}
            </span>
          </div>
          <div
            className="relative h-1 rounded-full overflow-hidden"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <motion.div
              className="absolute inset-y-0 left-0 rounded-full"
              style={{
                background:
                  vault.phase === "OPERATIONAL"
                    ? "linear-gradient(90deg, var(--emerald), rgba(0,255,136,0.6))"
                    : "linear-gradient(90deg, var(--amber-signal), rgba(245,158,11,0.5))",
                boxShadow:
                  vault.phase === "OPERATIONAL"
                    ? "0 0 8px var(--emerald)"
                    : "0 0 8px var(--amber-signal)",
              }}
              initial={{ width: 0 }}
              animate={{ width: `${fillPct * 100}%` }}
              transition={{ ...SPRING_HEAVY, delay: 0.3 + index * 0.1 }}
            />
          </div>
        </div>
        <div
          className="px-5 py-2.5 flex items-center justify-between"
          style={{
            background:
              vault.phase === "OPERATIONAL"
                ? "rgba(0,255,136,0.025)"
                : "rgba(245,158,11,0.025)",
            borderTop:
              vault.phase === "OPERATIONAL"
                ? "1px solid rgba(0,255,136,0.08)"
                : "1px solid rgba(245,158,11,0.08)",
          }}
        >
          <div className="flex items-center gap-2">
            <Dot
              color={vault.phase === "OPERATIONAL" ? "emerald" : "amber"}
              pulse={vault.phase === "OPERATIONAL"}
            />
            <span
              className="mono text-white/25 uppercase"
              style={{ fontSize: "9px", letterSpacing: "0.12em" }}
            >
              HAP: {vault.hapContractNumber}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {vault.closingDate && (
              <span
                className="mono text-white/20 uppercase"
                style={{ fontSize: "9px", letterSpacing: "0.1em" }}
              >
                CLOSED {fmt.date(vault.closingDate)}
              </span>
            )}
            <span
              className="mono uppercase font-black"
              style={{
                fontSize: "9px",
                color:
                  vault.phase === "OPERATIONAL"
                    ? "var(--emerald)"
                    : "var(--amber-signal)",
                letterSpacing: "0.14em",
              }}
            >
              {vault.hapBackstop}
            </span>
          </div>
        </div>
      </CornerBox>
    </motion.div>
  );
};

const PortfolioView = () => {
  const { currentUser, activeVaults, V2_CONSTANTS } = useSovereign();
  const myVaults = activeVaults
    .map((v) => ({
      vault: v,
      position: v.lpRoster.find((lp) => lp.id === currentUser.id),
    }))
    .filter((r) => !!r.position);
  const monthlyTotal = currentUser.preferredReturnAnnual / 12;
  return (
    <ViewShell viewKey="PORTFOLIO">
      <motion.div
        className="mb-6 space-y-2"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={STIFF_INSTITUTIONAL}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <PhaseBadge>
            LP SOVEREIGN YIELD PORTFOLIO // PRIVATE TREASURY
          </PhaseBadge>
          <StatusBadge type="green">YIELD ACTIVE</StatusBadge>
          <VerifiedBadge label="ACCREDITED INVESTOR" />
        </div>
        <h1
          className="text-white font-black uppercase leading-tight"
          style={{ fontSize: "26px", letterSpacing: "0.02em" }}
        >
          SOVEREIGN YIELD PORTFOLIO
          <br />
          <span style={{ color: "var(--emerald)", fontSize: "18px" }}>
            {currentUser.displayName}
          </span>
        </h1>
        <p
          className="mono text-white/25 uppercase"
          style={{ fontSize: "9px", letterSpacing: "0.2em" }}
        >
          {currentUser.accreditationType} · {currentUser.tier} TIER · REPORTING
          AS OF {fmt.date(new Date().toISOString())}
        </p>
      </motion.div>

      <motion.div
        className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
        variants={STAGGER_CONTAINER}
        initial="hidden"
        animate="visible"
      >
        {[
          {
            label: "TOTAL CAPITAL DEPLOYED",
            value: fmt.usd(currentUser.totalCommitted),
            sub: `${currentUser.totalVaults} ACTIVE VAULT POSITIONS`,
            highlight: true,
            trend: "up",
          },
          {
            label: "BLENDED NET YIELD",
            value: fmt.pct(V2_CONSTANTS.NET_YIELD_PCT),
            sub: "12.50% NET PREFERRED RETURN",
          },
          {
            label: "YTD CASH DISTRIBUTIONS",
            value: fmt.usd(YTD_DISTRIBUTIONS),
            sub: `FY2026 · ${
              ACH_LEDGER.filter((r) => r.status === "CLEARED").length
            } PAYMENTS CLEARED`,
            trend: "up",
          },
          {
            label: "NEXT ACH BATCH",
            value: "MAY 01",
            sub: "2026 · Q2 PREFERRED RETURN",
            accent: "amber",
          },
        ].map((card, i) => (
          <motion.div key={i} variants={STAGGER_CHILD}>
            <MetricCard {...card} />
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35, duration: 0.5 }}
        className="mb-5"
      >
        <CornerBox className="glass-emerald px-5 py-3 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Dot color="emerald" pulse />
            <span
              className="mono font-black uppercase"
              style={{
                fontSize: "10px",
                letterSpacing: "0.2em",
                color: "var(--emerald)",
              }}
            >
              YIELD ACCRUING LIVE
            </span>
            <span
              className="mono text-white/35 uppercase"
              style={{ fontSize: "9px", letterSpacing: "0.14em" }}
            >
              {fmt.usd(monthlyTotal)}/MO TOTAL
            </span>
          </div>
          <div className="flex items-center gap-4">
            {[
              {
                label: "ANNUAL PREF",
                value: fmt.usd(currentUser.preferredReturnAnnual),
              },
              { label: "MONTHLY", value: fmt.usd(monthlyTotal) },
              { label: "DAILY", value: `~${fmt.usd(monthlyTotal / 30)}` },
            ].map((s, i) => (
              <div key={i} className="text-right">
                <div
                  className="mono text-white/30 uppercase"
                  style={{ fontSize: "8px", letterSpacing: "0.12em" }}
                >
                  {s.label}
                </div>
                <div
                  className="mono font-black text-white"
                  style={{ fontSize: "11px" }}
                >
                  {s.value}
                </div>
              </div>
            ))}
          </div>
        </CornerBox>
      </motion.div>

      <motion.div
        className="mb-2"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...STIFF_INSTITUTIONAL, delay: 0.3 }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Label>ACTIVE VAULT ALLOCATIONS</Label>
            <span
              className="mono uppercase"
              style={{
                fontSize: "9px",
                color: "var(--emerald)",
                letterSpacing: "0.14em",
                background: "rgba(0,255,136,0.08)",
                border: "1px solid rgba(0,255,136,0.15)",
                padding: "2px 8px",
              }}
            >
              {myVaults.length} POSITIONS HELD
            </span>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="space-y-3 mb-6"
        variants={STAGGER_CONTAINER}
        initial="hidden"
        animate="visible"
      >
        {myVaults.map(({ vault, position }, i) => (
          <VaultAllocationCard
            key={vault.id}
            vault={vault}
            userPosition={position}
            index={i}
          />
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_HEAVY, delay: 0.45 }}
      >
        <div className="mb-3">
          <Label>ACH DISTRIBUTION LEDGER — PAYMENT HISTORY</Label>
        </div>
        <CornerBox className="glass-dark overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>DATE</th>
                <th>VAULT</th>
                <th>DESCRIPTION</th>
                <th>REF #</th>
                <th style={{ textAlign: "right" }}>AMOUNT</th>
                <th style={{ textAlign: "right" }}>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {ACH_LEDGER.map((row, i) => (
                <motion.tr
                  key={row.ref}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ ...STIFF_INSTITUTIONAL, delay: 0.5 + i * 0.06 }}
                >
                  <td className="text-white/50">{fmt.date(row.date)}</td>
                  <td>
                    <span
                      className="mono font-black text-white/70 uppercase"
                      style={{ fontSize: "10px" }}
                    >
                      {row.vault}
                    </span>
                  </td>
                  <td className="text-white/55 uppercase">{row.description}</td>
                  <td
                    className="mono text-white/25 uppercase"
                    style={{ fontSize: "9px", letterSpacing: "0.1em" }}
                  >
                    {row.ref}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <span
                      className="mono font-black"
                      style={{
                        color:
                          row.amount > 0
                            ? "var(--emerald)"
                            : "rgba(255,255,255,0.2)",
                        fontSize: "12px",
                      }}
                    >
                      {row.amount > 0 ? fmt.usd(row.amount) : "—"}
                    </span>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <StatusBadge
                      type={
                        row.status === "CLEARED"
                          ? "green"
                          : row.status === "PENDING"
                          ? "amber"
                          : "muted"
                      }
                    >
                      {row.status}
                    </StatusBadge>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
          <div
            className="flex items-center justify-between px-5 py-3"
            style={{
              borderTop: "1px solid rgba(255,255,255,0.06)",
              background: "rgba(0,255,136,0.025)",
            }}
          >
            <div className="flex items-center gap-3">
              <Dot color="emerald" />
              <span
                className="mono text-white/40 uppercase"
                style={{ fontSize: "9px", letterSpacing: "0.16em" }}
              >
                YTD TOTAL DISTRIBUTIONS
              </span>
            </div>
            <span
              className="mono font-black"
              style={{ fontSize: "15px", color: "var(--emerald)" }}
            >
              {fmt.usd(YTD_DISTRIBUTIONS)}
            </span>
          </div>
        </CornerBox>
      </motion.div>

      <motion.div
        className="mt-5 verdict-box"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.5 }}
      >
        <div
          className="text-center mono text-white/30 uppercase"
          style={{
            fontSize: "9px",
            letterSpacing: "0.16em",
            lineHeight: "1.9",
          }}
        >
          PREFERRED RETURNS ARE NON-CUMULATIVE AND SUBJECT TO AVAILABLE
          DISTRIBUTABLE CASH FLOW.
          <br />
          <span style={{ color: "var(--emerald)", fontWeight: "bold" }}>
            ALL POSITIONS GOVERNED BY REG D 506(c) SUBSCRIPTION AGREEMENT · SSX
            HOLDINGS LLC
          </span>
        </div>
      </motion.div>
    </ViewShell>
  );
};

// ─────────────────────────────────────────────────────────────
// § 8. DOCUMENT VAULT VIEW (Prompt 2 — Preserved verbatim)
// ─────────────────────────────────────────────────────────────
const DOCUMENT_REGISTRY = [
  {
    id: "DOC-001",
    category: "ENTITY STRUCTURE",
    title: "DELAWARE SERIES LLC OPERATING AGREEMENT",
    subtitle: "SSX VAULT SERIES 001 LLC · MASTER SERIES",
    status: "COUNTERSIGNED",
    statusType: "green",
    date: "2026-01-10",
    pages: 42,
    signatories: [
      "XAVIER SNOW · MANAGING MEMBER",
      "MERIDIAN CAPITAL FUND II · LP",
    ],
    docType: "OPERATING AGREEMENT",
    enclave: true,
  },
  {
    id: "DOC-002",
    category: "ENTITY STRUCTURE",
    title: "DELAWARE SERIES LLC OPERATING AGREEMENT",
    subtitle: "SSX VAULT SERIES 002 LLC · SERIES EXTENSION",
    status: "COUNTERSIGNED",
    statusType: "green",
    date: "2026-02-04",
    pages: 38,
    signatories: [
      "XAVIER SNOW · MANAGING MEMBER",
      "MERIDIAN CAPITAL FUND II · LP",
    ],
    docType: "OPERATING AGREEMENT",
    enclave: true,
  },
  {
    id: "DOC-003",
    category: "SUBSCRIPTION",
    title: "LP SUBSCRIPTION AGREEMENT — VAULT 001",
    subtitle: "$50,000 COMMITMENT · REG D 506(c) · ACCREDITED INVESTOR",
    status: "EXECUTED",
    statusType: "green",
    date: "2026-01-12",
    pages: 18,
    signatories: ["MERIDIAN CAPITAL FUND II · LP-001"],
    docType: "SUBSCRIPTION AGREEMENT",
    enclave: true,
  },
  {
    id: "DOC-004",
    category: "SUBSCRIPTION",
    title: "LP SUBSCRIPTION AGREEMENT — VAULT 002",
    subtitle: "$50,000 COMMITMENT · REG D 506(c) · ACCREDITED INVESTOR",
    status: "EXECUTED",
    statusType: "green",
    date: "2026-02-05",
    pages: 18,
    signatories: ["MERIDIAN CAPITAL FUND II · LP-001"],
    docType: "SUBSCRIPTION AGREEMENT",
    enclave: true,
  },
  {
    id: "DOC-005",
    category: "OFFERING DOCUMENTS",
    title: "PRIVATE PLACEMENT MEMORANDUM (PPM)",
    subtitle: "SSX SOVEREIGN VAULT FUND · REG D 506(c) · FY2026",
    status: "COUNTERSIGNED",
    statusType: "green",
    date: "2025-12-20",
    pages: 86,
    signatories: ["XAVIER SNOW · SSX HOLDINGS LLC"],
    docType: "PPM",
    enclave: true,
  },
  {
    id: "DOC-006",
    category: "TAX FORTRESS",
    title: "YEAR 1 COST SEGREGATION STUDY",
    subtitle: "1847 BENNING ROAD NE · VAULT 001 · GRANT THORNTON LLP",
    status: "DELIVERED",
    statusType: "green",
    date: "2026-03-15",
    pages: 24,
    signatories: ["GRANT THORNTON LLP · CPA"],
    docType: "COST SEGREGATION",
    enclave: true,
  },
  {
    id: "DOC-007",
    category: "TAX FORTRESS",
    title: "ANNUAL K-1 PASS-THROUGH — FY2025",
    subtitle: "MERIDIAN CAPITAL FUND II · SCHEDULE K-1 · FORM 1065",
    status: "PROCESSING",
    statusType: "amber",
    date: null,
    estimatedDate: "2026-05-15",
    pages: null,
    signatories: [],
    docType: "K-1 TAX DOCUMENT",
    enclave: false,
  },
  {
    id: "DOC-008",
    category: "KYC / COMPLIANCE",
    title: "ACCREDITED INVESTOR CERTIFICATION",
    subtitle: "MERIDIAN CAPITAL FUND II · REG D 506(c) · CPA VERIFIED",
    status: "VERIFIED",
    statusType: "green",
    date: "2026-01-08",
    pages: 4,
    signatories: ["GRANT THORNTON LLP · CPA VERIFICATION"],
    docType: "ACCREDITATION CERT",
    enclave: true,
  },
  {
    id: "DOC-009",
    category: "KYC / COMPLIANCE",
    title: "AML / KYC CLEARANCE CERTIFICATE",
    subtitle: "MERIDIAN CAPITAL FUND II · ID VERIFICATION · WATCHLIST CLEAR",
    status: "CLEARED",
    statusType: "green",
    date: "2026-01-08",
    pages: 2,
    signatories: ["SSX COMPLIANCE DESK"],
    docType: "AML CLEARANCE",
    enclave: false,
  },
];

const groupBy = (arr, key) =>
  arr.reduce((acc, item) => {
    (acc[item[key]] = acc[item[key]] || []).push(item);
    return acc;
  }, {});

const SecureDownloadBtn = ({
  available = true,
  label = "DOWNLOAD SECURE ENCLAVE",
}) => (
  <button className={cn("btn-secure", !available && "btn-secure-amber")}>
    <svg width="10" height="12" viewBox="0 0 10 12" fill="none">
      <rect
        x="1"
        y="5"
        width="8"
        height="7"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path
        d="M3 5V3.5C3 2.12 3.9 1 5 1C6.1 1 7 2.12 7 3.5V5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <circle cx="5" cy="8.5" r="1" fill="currentColor" />
    </svg>
    {label}
  </button>
);

const DocRow = ({ doc, index }) => {
  const isAmber = doc.statusType === "amber";
  return (
    <motion.div
      className="doc-row"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ ...STIFF_INSTITUTIONAL, delay: index * 0.05 }}
    >
      <div className="flex items-start justify-between px-5 py-4 gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div
            className="shrink-0 mt-0.5 flex items-center justify-center"
            style={{
              width: "32px",
              height: "36px",
              border: `1px solid ${
                isAmber ? "rgba(245,158,11,0.2)" : "rgba(0,255,136,0.15)"
              }`,
              background: isAmber
                ? "rgba(245,158,11,0.04)"
                : "rgba(0,255,136,0.04)",
            }}
          >
            <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
              <path
                d="M2 1H9L12 4V15H2V1Z"
                stroke={isAmber ? "var(--amber-signal)" : "var(--emerald)"}
                strokeWidth="1"
                strokeLinejoin="round"
                opacity="0.7"
              />
              <path
                d="M9 1V4H12"
                stroke={isAmber ? "var(--amber-signal)" : "var(--emerald)"}
                strokeWidth="1"
                strokeLinejoin="round"
                opacity="0.7"
              />
              <path
                d="M4 7H10M4 9.5H8"
                stroke={isAmber ? "var(--amber-signal)" : "var(--emerald)"}
                strokeWidth="1"
                strokeLinecap="round"
                opacity="0.5"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="mono font-black text-white uppercase"
              style={{ fontSize: "11px", letterSpacing: "0.08em" }}
            >
              {doc.title}
            </div>
            <div
              className="mono text-white/35 uppercase mt-0.5 truncate"
              style={{ fontSize: "9px", letterSpacing: "0.12em" }}
            >
              {doc.subtitle}
            </div>
            {doc.signatories.length > 0 && (
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <span
                  className="mono text-white/20 uppercase"
                  style={{ fontSize: "8px", letterSpacing: "0.1em" }}
                >
                  SIGNED:
                </span>
                {doc.signatories.map((s, i) => (
                  <span
                    key={i}
                    className="mono text-white/30 uppercase"
                    style={{ fontSize: "8px", letterSpacing: "0.08em" }}
                  >
                    {s}
                    {i < doc.signatories.length - 1 ? " ·" : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          {doc.pages && (
            <div className="text-right hidden md:block">
              <div
                className="mono text-white/20 uppercase"
                style={{ fontSize: "8px", letterSpacing: "0.12em" }}
              >
                PAGES
              </div>
              <div
                className="mono text-white/40 font-black"
                style={{ fontSize: "11px" }}
              >
                {doc.pages}
              </div>
            </div>
          )}
          <div className="text-right hidden md:block">
            <div
              className="mono text-white/20 uppercase"
              style={{ fontSize: "8px", letterSpacing: "0.12em" }}
            >
              {doc.date ? "EXECUTED" : "ETA"}
            </div>
            <div className="mono text-white/40" style={{ fontSize: "10px" }}>
              {doc.date
                ? fmt.date(doc.date)
                : doc.estimatedDate
                ? fmt.date(doc.estimatedDate)
                : "TBD"}
            </div>
          </div>
          <StatusBadge type={doc.statusType}>{doc.status}</StatusBadge>
          {doc.enclave ? (
            <SecureDownloadBtn available={!isAmber} />
          ) : (
            <div
              className="mono text-white/20 uppercase"
              style={{
                fontSize: "9px",
                letterSpacing: "0.12em",
                minWidth: "120px",
                textAlign: "center",
              }}
            >
              {isAmber
                ? "PROCESSING · ETA " +
                  (doc.estimatedDate ? fmt.date(doc.estimatedDate) : "TBD")
                : "AUTO-DELIVERED"}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const DocumentVaultView = () => {
  const { currentUser } = useSovereign();
  const [kycRevealed, setKycRevealed] = useState(false);
  const docGroups = groupBy(DOCUMENT_REGISTRY, "category");
  const categoryOrder = [
    "OFFERING DOCUMENTS",
    "ENTITY STRUCTURE",
    "SUBSCRIPTION",
    "KYC / COMPLIANCE",
    "TAX FORTRESS",
  ];
  return (
    <ViewShell viewKey="DOCUMENTS">
      <motion.div
        className="mb-6 space-y-2"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={STIFF_INSTITUTIONAL}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <PhaseBadge>SEC COMPLIANCE & LEGAL VAULT // REG D 506(c)</PhaseBadge>
          <StatusBadge type="green">ACCREDITED STATUS: VERIFIED</StatusBadge>
          <VerifiedBadge label="KYC CLEARED" />
        </div>
        <h1
          className="text-white font-black uppercase leading-tight"
          style={{ fontSize: "26px", letterSpacing: "0.02em" }}
        >
          SEC COMPLIANCE & LEGAL VAULT
          <br />
          <span style={{ color: "var(--emerald)", fontSize: "16px" }}>
            PPM · OPERATING AGREEMENTS · SUBSCRIPTION DOCS · TAX FORTRESS ·
            KYC/AML
          </span>
        </h1>
        <p
          className="mono text-white/25 uppercase"
          style={{ fontSize: "9px", letterSpacing: "0.2em" }}
        >
          DOCUMENTS SECURED IN SSX SOVEREIGN ENCLAVE · ENCRYPTED AT REST ·
          REGULATORY ARCHIVAL STANDARD
        </p>
      </motion.div>

      <motion.div
        className="mb-5"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_HEAVY, delay: 0.12 }}
      >
        <div className="mb-3 flex items-center justify-between">
          <Label>ACCREDITED INVESTOR PROFILE — KYC/AML CLEARANCE FILE</Label>
          <button
            className="btn-secure"
            style={{ fontSize: "8px", padding: "5px 12px" }}
            onClick={() => setKycRevealed(!kycRevealed)}
          >
            <svg width="9" height="11" viewBox="0 0 9 11" fill="none">
              <rect
                x="0.5"
                y="4.5"
                width="8"
                height="6"
                rx="1"
                stroke="currentColor"
                strokeWidth="1"
              />
              <path
                d="M2.5 4.5V3C2.5 1.9 3.4 1 4.5 1C5.6 1 6.5 1.9 6.5 3V4.5"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
              />
            </svg>
            {kycRevealed ? "LOCK PROFILE" : "UNLOCK PROFILE"}
          </button>
        </div>
        <CornerBox className="glass-dark overflow-hidden">
          <div
            className="flex items-start justify-between p-5"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
          >
            <div className="space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <StatusBadge type="green">AML STATUS: CLEAR</StatusBadge>
                <StatusBadge type="green">
                  KYC: TIER 3 INSTITUTIONAL
                </StatusBadge>
                <VerifiedBadge label="REG D 506(c) ACCREDITED" />
              </div>
              <div
                className={cn(
                  "text-white font-black uppercase",
                  kycRevealed && "decrypt-anim"
                )}
                style={{ fontSize: "17px", letterSpacing: "0.04em" }}
              >
                {currentUser.legalName}
              </div>
              <div
                className="mono text-white/40 uppercase"
                style={{ fontSize: "9px", letterSpacing: "0.14em" }}
              >
                {currentUser.entityType}
              </div>
            </div>
            <div className="text-right space-y-1 shrink-0 ml-4">
              <div
                className="mono font-black"
                style={{ fontSize: "13px", color: "var(--emerald)" }}
              >
                CLEARED
              </div>
              <div
                className="mono text-white/25 uppercase"
                style={{ fontSize: "8px", letterSpacing: "0.12em" }}
              >
                AML · {fmt.date(currentUser.amlClearanceDate)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4">
            {[
              {
                label: "ENTITY EIN",
                value: kycRevealed ? currentUser.ein : "●●-●●●●●●●",
                mono: true,
              },
              {
                label: "LIQUID ASSET TIER",
                value: currentUser.liquidAssetTier,
                em: true,
              },
              { label: "CPA VERIFICATION FIRM", value: currentUser.cpaFirm },
              {
                label: "CERT EXPIRATION",
                value: fmt.date(currentUser.cpaVerificationExpiry),
                mono: true,
              },
            ].map((field, fi) => (
              <div
                key={fi}
                className="p-4"
                style={{
                  borderRight:
                    fi < 3 ? "1px solid rgba(255,255,255,0.04)" : "none",
                  borderTop: "1px solid rgba(255,255,255,0.04)",
                }}
              >
                <Label>{field.label}</Label>
                <div
                  className={cn(
                    "mono font-black uppercase mt-1.5",
                    field.em ? "text-[var(--emerald)]" : "text-white",
                    kycRevealed && "decrypt-anim"
                  )}
                  style={{ fontSize: "12px", letterSpacing: "0.06em" }}
                >
                  {field.value}
                </div>
              </div>
            ))}
          </div>
          <div
            className="px-5 py-3 flex items-center gap-4 flex-wrap"
            style={{
              borderTop: "1px solid rgba(0,255,136,0.08)",
              background: "rgba(0,255,136,0.025)",
            }}
          >
            <div className="flex items-center gap-2">
              <Dot color="emerald" pulse />
              <span
                className="mono text-white/30 uppercase"
                style={{ fontSize: "9px", letterSpacing: "0.14em" }}
              >
                SUBSCRIBED ENTITIES:
              </span>
            </div>
            {currentUser.subscribedEntities.map((e, i) => (
              <span
                key={i}
                className="mono font-black uppercase"
                style={{
                  fontSize: "9px",
                  color: "var(--emerald)",
                  letterSpacing: "0.1em",
                }}
              >
                {e}
                {i < currentUser.subscribedEntities.length - 1 ? " ·" : ""}
              </span>
            ))}
          </div>
        </CornerBox>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_HEAVY, delay: 0.22 }}
        className="mb-3"
      >
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Label>DOCUMENT MATRIX — SOVEREIGN COMPLIANCE ARCHIVE</Label>
            <span
              className="mono uppercase"
              style={{
                fontSize: "9px",
                background: "rgba(0,255,136,0.08)",
                border: "1px solid rgba(0,255,136,0.15)",
                padding: "2px 8px",
                color: "var(--emerald)",
                letterSpacing: "0.14em",
              }}
            >
              {DOCUMENT_REGISTRY.length} DOCUMENTS INDEXED
            </span>
          </div>
          <div className="flex items-center gap-3">
            {[
              { color: "var(--emerald)", label: "EXECUTED / VERIFIED" },
              { color: "var(--amber-signal)", label: "PENDING / PROCESSING" },
            ].map((leg, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div
                  className="rounded-full"
                  style={{
                    width: "6px",
                    height: "6px",
                    background: leg.color,
                    boxShadow: `0 0 6px ${leg.color}`,
                  }}
                />
                <span
                  className="mono text-white/30 uppercase"
                  style={{ fontSize: "8px", letterSpacing: "0.12em" }}
                >
                  {leg.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      <div className="space-y-4">
        {categoryOrder.map((category, ci) => {
          const docs = docGroups[category];
          if (!docs?.length) return null;
          const allExecuted = docs.every((d) => d.statusType === "green");
          return (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...SPRING_HEAVY, delay: 0.28 + ci * 0.08 }}
            >
              <div className="flex items-center justify-between px-1 mb-2">
                <div className="flex items-center gap-3">
                  <Dot
                    color={allExecuted ? "emerald" : "amber"}
                    pulse={!allExecuted}
                  />
                  <span
                    className="mono font-black uppercase"
                    style={{
                      fontSize: "10px",
                      letterSpacing: "0.2em",
                      color: allExecuted
                        ? "rgba(255,255,255,0.55)"
                        : "var(--amber-signal)",
                    }}
                  >
                    {category}
                  </span>
                </div>
                <span
                  className="mono text-white/20 uppercase"
                  style={{ fontSize: "8px", letterSpacing: "0.12em" }}
                >
                  {docs.length} DOCUMENT{docs.length > 1 ? "S" : ""} ·{" "}
                  {docs.filter((d) => d.statusType === "green").length} EXECUTED
                </span>
              </div>
              <CornerBox className="glass-dark overflow-hidden">
                {docs.map((doc, di) => (
                  <DocRow key={doc.id} doc={doc} index={ci * 5 + di} />
                ))}
              </CornerBox>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        className="mt-5 verdict-box"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 0.5 }}
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div
              className="mono font-black uppercase mb-1"
              style={{
                fontSize: "10px",
                color: "var(--emerald)",
                letterSpacing: "0.18em",
              }}
            >
              SOVEREIGN ENCLAVE ENCRYPTION PROTOCOL
            </div>
            <div
              className="mono text-white/35 uppercase"
              style={{
                fontSize: "9px",
                letterSpacing: "0.14em",
                lineHeight: "1.8",
              }}
            >
              ALL DOCUMENTS STORED WITH AES-256 AT-REST ENCRYPTION · SSX
              SOVEREIGN ENCLAVE · 90-DAY DOWNLOAD TOKENS
              <br />
              SEC EDGAR COMPLIANCE READY · DELAWARE UCC RECORDABLE · IRS §1031
              ELIGIBLE
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <VerifiedBadge label="COMPLIANT" />
            <StatusBadge type="green">SEC REG D 506(c)</StatusBadge>
          </div>
        </div>
      </motion.div>
    </ViewShell>
  );
};

// ─────────────────────────────────────────────────────────────
/// ============================================================
// SSX SOVEREIGN TERMINAL — v4.0.0
// PROMPT 4 OF 4: THE GP COMMAND CENTER & COMPLIANCE MATRIX
// DROP-IN REPLACEMENT FOR <CommandView /> PLACEHOLDER ONLY.
// All imports and context hooks consumed from existing codebase.
// ============================================================

const CommandView = () => {
  const { activeVaults, systemMetrics, V2_CONSTANTS } = useSovereign();

  // ──────────────────────────────────────────────────────────
  // § LOCAL STATE MACHINES
  // ──────────────────────────────────────────────────────────

  // ACH Batch processing: "IDLE" | "PROCESSING" | "CONFIRMED"
  const [achBatchStage, setAchBatchStage] = useState("IDLE");
  // DCHA sync: "IDLE" | "SYNCING" | "SYNCED"
  const [dchaSync, setDchaSync] = useState("IDLE");
  // K-1 gen: "IDLE" | "GENERATING" | "COMPLETE"
  const [k1Stage, setK1Stage] = useState("IDLE");
  // Wire clearance: tracks which wire IDs have been cleared
  const [clearedWires, setClearedWires] = useState([]);
  // LP approval state: { [id]: "APPROVED" | "REJECTED" }
  const [lpDecisions, setLpDecisions] = useState({});
  // Vault wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStage, setWizardStage] = useState("FORM"); // "FORM"|"DEPLOYING"|"LIVE"
  const [wizardForm, setWizardForm] = useState({
    address: "",
    city: "WASHINGTON, D.C.",
    zip: "",
    sqft: "3200",
    hapEstimate: "8250",
    closingEta: "",
  });
  const [wizardErrors, setWizardErrors] = useState({});

  // ──────────────────────────────────────────────────────────
  // § MOCK DATA — LOCALIZED TO CommandView
  // ──────────────────────────────────────────────────────────

  // Incoming DCHA HAP wires awaiting operator clearance and routing
  const PENDING_HAP_WIRES = [
    {
      id: "WIRE-2026-05-001",
      vault: "SSX-VAULT-001",
      address: "1847 BENNING ROAD NE",
      amount: 8_250.0,
      source: "DCHA · DISTRICT OF COLUMBIA HOUSING AUTHORITY",
      receivedDate: "2026-05-01",
      hapContract: "HAP-2026-DC-4471",
      month: "MAY 2026",
      status: "PENDING CLEARANCE",
      lpCount: 6,
      perLpDistribution: (8_250 * 0.85) / 6, // After platform fee: ~$1,168.75/LP
    },
    {
      id: "WIRE-2026-05-002",
      vault: "SSX-VAULT-002",
      amount: 8_250.0,
      address: "3312 SHERIFF ROAD NE",
      source: "DCHA · DISTRICT OF COLUMBIA HOUSING AUTHORITY",
      receivedDate: "2026-05-01",
      hapContract: "HAP-2026-DC-4512",
      month: "MAY 2026",
      status: "PENDING CLEARANCE",
      lpCount: 4,
      perLpDistribution: (8_250 * 0.85) / 4,
    },
    {
      id: "WIRE-2026-04-001",
      vault: "SSX-VAULT-001",
      address: "1847 BENNING ROAD NE",
      amount: 8_250.0,
      source: "DCHA · DISTRICT OF COLUMBIA HOUSING AUTHORITY",
      receivedDate: "2026-04-01",
      hapContract: "HAP-2026-DC-4471",
      month: "APR 2026",
      status: "CLEARED",
      lpCount: 6,
      perLpDistribution: (8_250 * 0.85) / 6,
    },
  ];

  // LP allocation clearance queue from Exchange interactions
  const PENDING_LP_APPROVALS = [
    {
      id: "ALLOC-2026-8F2A1C",
      submittedDate: "2026-04-14T16:42:00Z",
      vault: "SSX-VAULT-002",
      vaultAddress: "3312 SHERIFF ROAD NE",
      legalName: "THORNFIELD CAPITAL PARTNERS III, LP",
      entityType: "DELAWARE LIMITED PARTNERSHIP",
      requestedAmount: 50_000,
      accredBasis: "CPA VERIFIED ACCREDITATION",
      tier: "INSTITUTIONAL",
      kycFlag: false,
      amlFlag: false,
      projectedReturn: 50_000 * V2_CONSTANTS.NET_YIELD_PCT,
    },
    {
      id: "ALLOC-2026-C9D44E",
      submittedDate: "2026-04-13T09:22:00Z",
      vault: "SSX-VAULT-002",
      vaultAddress: "3312 SHERIFF ROAD NE",
      legalName: "ELARA SOVEREIGN TRUST",
      entityType: "IRREVOCABLE TRUST",
      requestedAmount: 50_000,
      accredBasis: "ACCREDITED INVESTOR — NET WORTH TEST",
      tier: "HIGH NET WORTH",
      kycFlag: false,
      amlFlag: true,
      projectedReturn: 50_000 * V2_CONSTANTS.NET_YIELD_PCT,
    },
    {
      id: "ALLOC-2026-A1F773",
      submittedDate: "2026-04-11T14:05:00Z",
      vault: "SSX-VAULT-003",
      vaultAddress: "5508 EASTERN AVENUE NE",
      legalName: "VANTAGE POINT FAMILY OFFICE LLC",
      entityType: "DELAWARE LLC",
      requestedAmount: 50_000,
      accredBasis: "QUALIFIED PURCHASER — $5M+ INVESTMENTS",
      tier: "INSTITUTIONAL",
      kycFlag: false,
      amlFlag: false,
      projectedReturn: 50_000 * V2_CONSTANTS.NET_YIELD_PCT,
    },
  ];

  // Compliance matrix rows derived from activeVaults
  const COMPLIANCE_ROWS = [
    {
      vaultId: "SSX-VAULT-001",
      shortId: "V001",
      address: "1847 BENNING ROAD NE",
      phase: "OPERATIONAL",
      hqsCert: { status: "CERTIFIED", type: "green", expiry: "2027-01-15" },
      hapStatus: { status: "ACTIVE", type: "green", note: "HAP-2026-DC-4471" },
      k1Status: { status: "FILED", type: "green", note: "FY2025 · DELIVERED" },
      action: { status: "CLEAR", type: "green" },
    },
    {
      vaultId: "SSX-VAULT-002",
      shortId: "V002",
      address: "3312 SHERIFF ROAD NE",
      phase: "FUNDING",
      hqsCert: {
        status: "SCHEDULED",
        type: "amber",
        expiry: "PENDING · MAY 2026",
      },
      hapStatus: {
        status: "PENDING",
        type: "amber",
        note: "HAP-2026-DC-4512 · AWAITING EXEC",
      },
      k1Status: { status: "Q4 2026", type: "muted", note: "NOT YET TRIGGERED" },
      action: { status: "ACTION REQ", type: "amber" },
    },
    {
      vaultId: "SSX-VAULT-003",
      shortId: "V003",
      address: "5508 EASTERN AVENUE NE",
      phase: "PIPELINE",
      hqsCert: { status: "NOT FILED", type: "muted", expiry: "PIPELINE STAGE" },
      hapStatus: {
        status: "APPLICATION",
        type: "amber",
        note: "CASE DCHA-2026-APP-7741",
      },
      k1Status: { status: "N/A", type: "muted", note: "PRE-OPERATIONAL" },
      action: { status: "MONITOR", type: "gold" },
    },
  ];

  // ──────────────────────────────────────────────────────────
  // § DERIVED METRICS (Command Center specific)
  // ──────────────────────────────────────────────────────────
  // Raise-based AUM: assumes 10 vaults at full $300K raise (platform target)
  const PLATFORM_TARGET_VAULTS = 10;
  const raiseBasedAUM = PLATFORM_TARGET_VAULTS * V2_CONSTANTS.RAISE_PER_VAULT;
  // Platform ARR = 1% of raise-based AUM proxy (management fee layer)
  const platformARR = raiseBasedAUM * 0.01;
  // Monthly HAP Anchor = operational vaults × monthly HAP
  const monthlyHAPAnchor = activeVaults
    .filter((v) => v.phase === "OPERATIONAL")
    .reduce((s, v) => s + (v.monthlyHAP || V2_CONSTANTS.MONTHLY_HAP), 0);
  // OCF split denominator (after LP preferred return)
  const annualOCF = activeVaults
    .filter((v) => v.phase === "OPERATIONAL")
    .reduce((s, v) => s + v.grossRevenueAnnual - v.preferredReturnAnnual, 0);
  const xavierSplit = annualOCF * 0.6;
  const joeSplit = annualOCF * 0.4;

  // Pending wire total
  const pendingWireTotal = PENDING_HAP_WIRES.filter(
    (w) => w.status === "PENDING CLEARANCE" && !clearedWires.includes(w.id)
  ).reduce((s, w) => s + w.amount, 0);

  const pendingApprovalCount = PENDING_LP_APPROVALS.filter(
    (lp) => !lpDecisions[lp.id]
  ).length;

  // ──────────────────────────────────────────────────────────
  // § HANDLER FUNCTIONS
  // ──────────────────────────────────────────────────────────
  const handleClearWire = (wireId) => {
    setClearedWires((prev) => [...prev, wireId]);
  };

  const handleLpDecision = (id, decision) => {
    setLpDecisions((prev) => ({ ...prev, [id]: decision }));
  };

  const handleAchBatch = () => {
    if (achBatchStage !== "IDLE") return;
    setAchBatchStage("PROCESSING");
    setTimeout(() => setAchBatchStage("CONFIRMED"), 2800);
    setTimeout(() => setAchBatchStage("IDLE"), 6000);
  };

  const handleDchaSync = () => {
    if (dchaSync !== "IDLE") return;
    setDchaSync("SYNCING");
    setTimeout(() => setDchaSync("SYNCED"), 2200);
    setTimeout(() => setDchaSync("IDLE"), 5500);
  };

  const handleK1Gen = () => {
    if (k1Stage !== "IDLE") return;
    setK1Stage("GENERATING");
    setTimeout(() => setK1Stage("COMPLETE"), 3200);
    setTimeout(() => setK1Stage("IDLE"), 7000);
  };

  const handleWizardSubmit = () => {
    const errs = {};
    if (!wizardForm.address.trim()) errs.address = "REQUIRED";
    if (!wizardForm.zip.trim()) errs.zip = "REQUIRED";
    if (!wizardForm.closingEta.trim()) errs.closingEta = "REQUIRED";
    if (Object.keys(errs).length > 0) {
      setWizardErrors(errs);
      return;
    }
    setWizardStage("DEPLOYING");
    setTimeout(() => setWizardStage("LIVE"), 3000);
  };

  const resetWizard = () => {
    setWizardStage("FORM");
    setWizardOpen(false);
    setWizardForm({
      address: "",
      city: "WASHINGTON, D.C.",
      zip: "",
      sqft: "3200",
      hapEstimate: "8250",
      closingEta: "",
    });
    setWizardErrors({});
  };

  // ──────────────────────────────────────────────────────────
  // § RENDER
  // ──────────────────────────────────────────────────────────
  return (
    <ViewShell viewKey="COMMAND">
      {/* ═══════════════════════════════════════════════════
          HEADER
      ═══════════════════════════════════════════════════ */}
      <motion.div
        className="mb-6 space-y-2"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={STIFF_INSTITUTIONAL}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <PhaseBadge>SPONSOR COMMAND CENTER // GP OPERATOR CONSOLE</PhaseBadge>
          <StatusBadge type="green">ALL SYSTEMS ACTIVE</StatusBadge>
          <StatusBadge type="muted">RESTRICTED ACCESS</StatusBadge>
        </div>
        <h1
          className="text-white font-black uppercase leading-tight"
          style={{ fontSize: "26px", letterSpacing: "0.02em" }}
        >
          SPONSOR COMMAND CENTER
          <br />
          <span style={{ color: "var(--emerald)", fontSize: "15px" }}>
            XAVIER SNOW & DR. JOSEPH ASAMOAH · FOUNDING PARTNERS · SSX HOLDINGS
            LLC
          </span>
        </h1>
        <p
          className="mono text-white/25 uppercase"
          style={{ fontSize: "9px", letterSpacing: "0.2em" }}
        >
          GP ADMINISTRATIVE LAYER · HAP WIRE ROUTING · LP ONBOARDING · VAULT
          DEPLOYMENT · COMPLIANCE MATRIX · OCF DISTRIBUTION ENGINE
        </p>
      </motion.div>

      {/* ═══════════════════════════════════════════════════
          TOP METRICS GRID
      ═══════════════════════════════════════════════════ */}
      <motion.div
        className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
        variants={STAGGER_CONTAINER}
        initial="hidden"
        animate="visible"
      >
        {[
          {
            label: "RAISE-BASED AUM TARGET",
            value: fmt.usd(raiseBasedAUM),
            sub: `${PLATFORM_TARGET_VAULTS} VAULTS × ${fmt.usd(
              V2_CONSTANTS.RAISE_PER_VAULT
            )}`,
            highlight: false,
            trend: "up",
          },
          {
            label: "PLATFORM ARR (1% FEE)",
            value: fmt.usd(platformARR),
            sub: "ANNUAL MANAGEMENT REVENUE",
            highlight: true,
            trend: "up",
          },
          {
            label: "MONTHLY HAP ANCHOR",
            value: fmt.usd(monthlyHAPAnchor),
            sub: `${
              activeVaults.filter((v) => v.phase === "OPERATIONAL").length
            } OPERATIONAL VAULT${
              activeVaults.filter((v) => v.phase === "OPERATIONAL").length !== 1
                ? "S"
                : ""
            }`,
            highlight: false,
          },
          {
            label: "OCF SPLIT (XAVIER / JOE)",
            value: "60 / 40",
            sub: `${fmt.usd(xavierSplit)} / ${fmt.usd(joeSplit)} YR`,
            highlight: false,
            accent: "amber",
          },
        ].map((card, i) => (
          <motion.div key={i} variants={STAGGER_CHILD}>
            <MetricCard {...card} />
          </motion.div>
        ))}
      </motion.div>

      {/* ═══════════════════════════════════════════════════
          DISTRIBUTION WATERFALL
      ═══════════════════════════════════════════════════ */}
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_HEAVY, delay: 0.18 }}
      >
        <div className="mb-3 flex items-center justify-between">
          <Label>
            AUTOMATED DISTRIBUTION WATERFALL — PER VAULT · MONTHLY HAP CYCLE
          </Label>
          <span
            className="mono text-white/25 uppercase"
            style={{ fontSize: "8px", letterSpacing: "0.14em" }}
          >
            PRIORITY-ORDERED ROUTING · CONTRACT-ENFORCED
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* PRIORITY 1 */}
          <motion.div
            variants={STAGGER_CHILD}
            initial="hidden"
            animate="visible"
          >
            <CornerBox
              className="glass-dark overflow-hidden card-hover"
              style={{ borderColor: "rgba(0,255,136,0.15)" }}
            >
              <div
                className="px-5 py-3 flex items-center gap-2"
                style={{
                  borderBottom: "1px solid rgba(0,255,136,0.1)",
                  background: "rgba(0,255,136,0.04)",
                }}
              >
                <div
                  className="flex items-center justify-center mono font-black"
                  style={{
                    width: "22px",
                    height: "22px",
                    border: "1px solid rgba(0,255,136,0.35)",
                    background: "rgba(0,255,136,0.08)",
                    fontSize: "10px",
                    color: "var(--emerald)",
                    flexShrink: 0,
                  }}
                >
                  01
                </div>
                <span
                  className="mono font-black uppercase"
                  style={{
                    fontSize: "9px",
                    color: "var(--emerald)",
                    letterSpacing: "0.18em",
                  }}
                >
                  PRIORITY 1 · SENIOR
                </span>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <div
                    className="mono font-black text-white uppercase"
                    style={{ fontSize: "12px", letterSpacing: "0.1em" }}
                  >
                    SSX PLATFORM ARR
                  </div>
                  <div
                    className="mono text-white/40 uppercase mt-1"
                    style={{ fontSize: "9px", letterSpacing: "0.12em" }}
                  >
                    ORIGINATION · MANAGEMENT · ADMIN FEES
                  </div>
                </div>
                <div className="glow-line" />
                <div className="space-y-2">
                  {[
                    {
                      label: "ORIGINATION FEE",
                      value: fmt.usd(V2_CONSTANTS.ORIGINATION_FEE),
                      note: "10% OF RAISE · ONE-TIME",
                    },
                    {
                      label: "PLATFORM FEE",
                      value: "15% OF HAP",
                      note: `~${fmt.usd(monthlyHAPAnchor * 0.15)}/MO`,
                    },
                  ].map((row, ri) => (
                    <div key={ri} className="flex items-center justify-between">
                      <span
                        className="mono text-white/40 uppercase"
                        style={{ fontSize: "8px", letterSpacing: "0.1em" }}
                      >
                        {row.label}
                      </span>
                      <div className="text-right">
                        <div
                          className="mono font-black"
                          style={{ fontSize: "10px", color: "var(--emerald)" }}
                        >
                          {row.value}
                        </div>
                        <div
                          className="mono text-white/25 uppercase"
                          style={{ fontSize: "7px", letterSpacing: "0.08em" }}
                        >
                          {row.note}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  className="p-3 flex items-center justify-between"
                  style={{
                    background: "rgba(0,255,136,0.04)",
                    border: "1px solid rgba(0,255,136,0.1)",
                  }}
                >
                  <Dot color="emerald" pulse />
                  <span
                    className="mono font-black text-white/70 uppercase"
                    style={{ fontSize: "9px", letterSpacing: "0.12em" }}
                  >
                    CLEARED FIRST
                  </span>
                  <StatusBadge type="green">SENIOR LIEN</StatusBadge>
                </div>
              </div>
            </CornerBox>
          </motion.div>

          {/* PRIORITY 2 */}
          <motion.div
            variants={STAGGER_CHILD}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.08 }}
          >
            <CornerBox
              className="glass-dark overflow-hidden card-hover"
              style={{ borderColor: "rgba(0,170,255,0.12)" }}
            >
              <div
                className="px-5 py-3 flex items-center gap-2"
                style={{
                  borderBottom: "1px solid rgba(0,170,255,0.1)",
                  background: "rgba(0,170,255,0.04)",
                }}
              >
                <div
                  className="flex items-center justify-center mono font-black"
                  style={{
                    width: "22px",
                    height: "22px",
                    border: "1px solid rgba(0,170,255,0.35)",
                    background: "rgba(0,170,255,0.08)",
                    fontSize: "10px",
                    color: "#00aaff",
                    flexShrink: 0,
                  }}
                >
                  02
                </div>
                <span
                  className="mono font-black uppercase"
                  style={{
                    fontSize: "9px",
                    color: "#00aaff",
                    letterSpacing: "0.18em",
                  }}
                >
                  PRIORITY 2 · LP PREFERRED
                </span>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <div
                    className="mono font-black text-white uppercase"
                    style={{ fontSize: "12px", letterSpacing: "0.1em" }}
                  >
                    LP NET PREF (12.50%)
                  </div>
                  <div
                    className="mono text-white/40 uppercase mt-1"
                    style={{ fontSize: "9px", letterSpacing: "0.12em" }}
                  >
                    PREFERRED RETURN · 6 LPs PER VAULT
                  </div>
                </div>
                <div
                  style={{
                    height: "1px",
                    background:
                      "linear-gradient(90deg, transparent, #00aaff, transparent)",
                    opacity: 0.3,
                  }}
                />
                <div className="space-y-2">
                  {[
                    {
                      label: "ANNUAL PREF / VAULT",
                      value: fmt.usd(
                        V2_CONSTANTS.RAISE_PER_VAULT *
                          V2_CONSTANTS.NET_YIELD_PCT
                      ),
                      note: "12.50% ON $300K",
                    },
                    {
                      label: "MONTHLY / LP",
                      value: fmt.usd(
                        (V2_CONSTANTS.LP_MIN_TICKET *
                          V2_CONSTANTS.NET_YIELD_PCT) /
                          12
                      ),
                      note: "PER $50K POSITION",
                    },
                  ].map((row, ri) => (
                    <div key={ri} className="flex items-center justify-between">
                      <span
                        className="mono text-white/40 uppercase"
                        style={{ fontSize: "8px", letterSpacing: "0.1em" }}
                      >
                        {row.label}
                      </span>
                      <div className="text-right">
                        <div
                          className="mono font-black"
                          style={{ fontSize: "10px", color: "#00aaff" }}
                        >
                          {row.value}
                        </div>
                        <div
                          className="mono text-white/25 uppercase"
                          style={{ fontSize: "7px", letterSpacing: "0.08em" }}
                        >
                          {row.note}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  className="p-3 flex items-center justify-between"
                  style={{
                    background: "rgba(0,170,255,0.04)",
                    border: "1px solid rgba(0,170,255,0.1)",
                  }}
                >
                  <div
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      background: "#00aaff",
                      boxShadow: "0 0 8px #00aaff",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    className="mono font-black text-white/70 uppercase"
                    style={{ fontSize: "9px", letterSpacing: "0.12em" }}
                  >
                    ACH DISTRIBUTED
                  </span>
                  <StatusBadge type="blue">NON-CUMULATIVE</StatusBadge>
                </div>
              </div>
            </CornerBox>
          </motion.div>

          {/* PRIORITY 3 */}
          <motion.div
            variants={STAGGER_CHILD}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.16 }}
          >
            <CornerBox
              className="glass-dark overflow-hidden card-hover"
              style={{ borderColor: "rgba(255,136,170,0.12)" }}
            >
              <div
                className="px-5 py-3 flex items-center gap-2"
                style={{
                  borderBottom: "1px solid rgba(255,136,170,0.1)",
                  background: "rgba(255,136,170,0.03)",
                }}
              >
                <div
                  className="flex items-center justify-center mono font-black"
                  style={{
                    width: "22px",
                    height: "22px",
                    border: "1px solid rgba(255,136,170,0.35)",
                    background: "rgba(255,136,170,0.06)",
                    fontSize: "10px",
                    color: "#ff88aa",
                    flexShrink: 0,
                  }}
                >
                  03
                </div>
                <span
                  className="mono font-black uppercase"
                  style={{
                    fontSize: "9px",
                    color: "#ff88aa",
                    letterSpacing: "0.18em",
                  }}
                >
                  PRIORITY 3 · OCF SPLIT
                </span>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <div
                    className="mono font-black text-white uppercase"
                    style={{ fontSize: "12px", letterSpacing: "0.1em" }}
                  >
                    OPERATING CASH FLOW
                  </div>
                  <div
                    className="mono text-white/40 uppercase mt-1"
                    style={{ fontSize: "9px", letterSpacing: "0.12em" }}
                  >
                    RESIDUAL AFTER FEES + LP PREF
                  </div>
                </div>
                <div
                  style={{
                    height: "1px",
                    background:
                      "linear-gradient(90deg, transparent, #ff88aa, transparent)",
                    opacity: 0.3,
                  }}
                />
                <div className="space-y-2">
                  {[
                    {
                      label: "XAVIER SNOW (60%)",
                      value: fmt.usd(xavierSplit),
                      note: "MANAGING MEMBER",
                    },
                    {
                      label: "DR. JOSEPH ASAMOAH (40%)",
                      value: fmt.usd(joeSplit),
                      note: "ADVISORY · PIUs",
                    },
                  ].map((row, ri) => (
                    <div key={ri} className="flex items-center justify-between">
                      <span
                        className="mono text-white/40 uppercase"
                        style={{ fontSize: "8px", letterSpacing: "0.1em" }}
                      >
                        {row.label}
                      </span>
                      <div className="text-right">
                        <div
                          className="mono font-black"
                          style={{ fontSize: "10px", color: "#ff88aa" }}
                        >
                          {row.value}
                        </div>
                        <div
                          className="mono text-white/25 uppercase"
                          style={{ fontSize: "7px", letterSpacing: "0.08em" }}
                        >
                          {row.note}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  className="p-3 flex items-center justify-between"
                  style={{
                    background: "rgba(255,136,170,0.03)",
                    border: "1px solid rgba(255,136,170,0.1)",
                  }}
                >
                  <div
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      background: "#ff88aa",
                      boxShadow: "0 0 8px #ff88aa",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    className="mono font-black text-white/70 uppercase"
                    style={{ fontSize: "9px", letterSpacing: "0.12em" }}
                  >
                    RESIDUAL ONLY
                  </span>
                  <StatusBadge type="muted">MILESTONE-GATED</StatusBadge>
                </div>
              </div>
            </CornerBox>
          </motion.div>
        </div>
      </motion.div>

      {/* ═══════════════════════════════════════════════════
          MAIN TWO-COLUMN GRID
      ═══════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* ─────────────────────────────────────────────────
            MODULE 1 — SHADOW LEDGER: HAP WIRE CLEARANCE
        ───────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...SPRING_HEAVY, delay: 0.22 }}
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label>SHADOW LEDGER · DCHA HAP WIRE CLEARANCE</Label>
              {pendingWireTotal > 0 && (
                <motion.span
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 1.8, repeat: Infinity }}
                  className="mono font-black uppercase"
                  style={{
                    fontSize: "8px",
                    color: "var(--amber-signal)",
                    background: "rgba(245,158,11,0.08)",
                    border: "1px solid rgba(245,158,11,0.2)",
                    padding: "2px 8px",
                    letterSpacing: "0.14em",
                  }}
                >
                  {fmt.usd(pendingWireTotal)} PENDING ROUTING
                </motion.span>
              )}
            </div>
          </div>

          <CornerBox className="glass-dark overflow-hidden">
            {/* Ledger header */}
            <div
              className="grid px-4 py-2.5"
              style={{
                gridTemplateColumns: "1fr 90px 90px 90px",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                gap: "8px",
              }}
            >
              {["VAULT / WIRE", "AMOUNT", "STATUS", "ACTION"].map((h, hi) => (
                <div
                  key={hi}
                  className="mono text-white/25 uppercase"
                  style={{
                    fontSize: "8px",
                    letterSpacing: "0.14em",
                    textAlign: hi > 1 ? "center" : "left",
                  }}
                >
                  {h}
                </div>
              ))}
            </div>

            {PENDING_HAP_WIRES.map((wire, wi) => {
              const isCleared =
                wire.status === "CLEARED" || clearedWires.includes(wire.id);
              return (
                <motion.div
                  key={wire.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    ...STIFF_INSTITUTIONAL,
                    delay: 0.25 + wi * 0.07,
                  }}
                  className="grid px-4 py-3.5"
                  style={{
                    gridTemplateColumns: "1fr 90px 90px 90px",
                    borderBottom:
                      wi < PENDING_HAP_WIRES.length - 1
                        ? "1px solid rgba(255,255,255,0.04)"
                        : "none",
                    background: isCleared
                      ? "rgba(0,255,136,0.02)"
                      : "transparent",
                    alignItems: "center",
                    gap: "8px",
                    transition: "background 0.3s ease",
                  }}
                >
                  {/* Vault Info */}
                  <div className="flex items-start gap-2">
                    <Dot
                      color={isCleared ? "emerald" : "amber"}
                      pulse={!isCleared}
                    />
                    <div className="min-w-0">
                      <div
                        className="mono font-black text-white uppercase"
                        style={{ fontSize: "10px", letterSpacing: "0.08em" }}
                      >
                        {wire.vault} · {wire.month}
                      </div>
                      <div
                        className="mono text-white/30 uppercase truncate"
                        style={{ fontSize: "8px", letterSpacing: "0.08em" }}
                      >
                        {wire.address}
                      </div>
                      <div
                        className="mono text-white/20 uppercase"
                        style={{ fontSize: "7px", letterSpacing: "0.08em" }}
                      >
                        {wire.id}
                      </div>
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="text-right">
                    <div
                      className="mono font-black"
                      style={{
                        fontSize: "12px",
                        color: isCleared
                          ? "var(--emerald)"
                          : "rgba(255,255,255,0.8)",
                      }}
                    >
                      {fmt.usd(wire.amount)}
                    </div>
                    <div
                      className="mono text-white/20 uppercase"
                      style={{ fontSize: "7px" }}
                    >
                      {wire.lpCount} LPs
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex justify-center">
                    <StatusBadge type={isCleared ? "green" : "amber"}>
                      {isCleared ? "CLEARED" : "PENDING"}
                    </StatusBadge>
                  </div>

                  {/* Action */}
                  <div className="flex justify-center">
                    {isCleared ? (
                      <span
                        className="mono text-white/20 uppercase"
                        style={{ fontSize: "8px", letterSpacing: "0.1em" }}
                      >
                        ROUTED ✓
                      </span>
                    ) : (
                      <button
                        onClick={() => handleClearWire(wire.id)}
                        className="mono font-black uppercase transition-all duration-200"
                        style={{
                          fontSize: "8px",
                          letterSpacing: "0.14em",
                          color: "var(--emerald)",
                          background: "rgba(0,255,136,0.07)",
                          border: "1px solid rgba(0,255,136,0.25)",
                          padding: "4px 10px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "rgba(0,255,136,0.14)";
                          e.currentTarget.style.borderColor =
                            "rgba(0,255,136,0.5)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            "rgba(0,255,136,0.07)";
                          e.currentTarget.style.borderColor =
                            "rgba(0,255,136,0.25)";
                        }}
                      >
                        CLEAR
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}

            {/* Process Distribution CTA */}
            <div
              className="p-4"
              style={{
                borderTop: "1px solid rgba(255,255,255,0.06)",
                background: "rgba(0,255,136,0.02)",
              }}
            >
              <button
                onClick={handleAchBatch}
                disabled={achBatchStage !== "IDLE"}
                className="w-full flex items-center justify-center gap-3 mono font-black uppercase transition-all duration-200"
                style={{
                  fontSize: "10px",
                  letterSpacing: "0.22em",
                  padding: "12px",
                  border:
                    achBatchStage === "CONFIRMED"
                      ? "1px solid rgba(0,255,136,0.5)"
                      : achBatchStage === "PROCESSING"
                      ? "1px solid rgba(245,158,11,0.3)"
                      : "1px solid rgba(0,255,136,0.3)",
                  background:
                    achBatchStage === "CONFIRMED"
                      ? "rgba(0,255,136,0.1)"
                      : achBatchStage === "PROCESSING"
                      ? "rgba(245,158,11,0.05)"
                      : "rgba(0,255,136,0.06)",
                  color:
                    achBatchStage === "CONFIRMED"
                      ? "var(--emerald)"
                      : achBatchStage === "PROCESSING"
                      ? "var(--amber-signal)"
                      : "var(--emerald)",
                  cursor: achBatchStage !== "IDLE" ? "not-allowed" : "pointer",
                  opacity: achBatchStage === "PROCESSING" ? 0.75 : 1,
                }}
              >
                {achBatchStage === "IDLE" && (
                  <>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M6 1L10 5H7.5V9H4.5V5H2L6 1Z"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinejoin="round"
                      />
                      <rect
                        x="1"
                        y="10.5"
                        width="10"
                        height="1"
                        rx="0.5"
                        fill="currentColor"
                        opacity="0.4"
                      />
                    </svg>
                    PROCESS ACH DISTRIBUTION BATCH
                  </>
                )}
                {achBatchStage === "PROCESSING" && (
                  <>
                    <motion.div
                      style={{
                        width: "10px",
                        height: "10px",
                        border: "1.5px solid var(--amber-signal)",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                      }}
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                    ROUTING TO LP ACCOUNTS · DO NOT CLOSE
                  </>
                )}
                {achBatchStage === "CONFIRMED" && (
                  <>
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path
                        d="M1 5L4.5 8.5L11 1"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    BATCH CONFIRMED · LPs NOTIFIED
                  </>
                )}
              </button>
            </div>
          </CornerBox>
        </motion.div>

        {/* ─────────────────────────────────────────────────
            MODULE 2 — LP ONBOARDING & CLEARANCE QUEUE
        ───────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...SPRING_HEAVY, delay: 0.3 }}
        >
          <div className="mb-3 flex items-center justify-between">
            <Label>LP ONBOARDING QUEUE · CLEARANCE PENDING</Label>
            {pendingApprovalCount > 0 && (
              <span
                className="mono font-black uppercase"
                style={{
                  fontSize: "8px",
                  color: "var(--amber-signal)",
                  background: "rgba(245,158,11,0.08)",
                  border: "1px solid rgba(245,158,11,0.2)",
                  padding: "2px 8px",
                  letterSpacing: "0.14em",
                }}
              >
                {pendingApprovalCount} AWAITING DECISION
              </span>
            )}
          </div>

          <CornerBox className="glass-dark overflow-hidden">
            {PENDING_LP_APPROVALS.map((lp, li) => {
              const decision = lpDecisions[lp.id];
              const isPending = !decision;

              return (
                <motion.div
                  key={lp.id}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    ...STIFF_INSTITUTIONAL,
                    delay: 0.3 + li * 0.08,
                  }}
                  style={{
                    borderBottom:
                      li < PENDING_LP_APPROVALS.length - 1
                        ? "1px solid rgba(255,255,255,0.05)"
                        : "none",
                    background:
                      decision === "APPROVED"
                        ? "rgba(0,255,136,0.03)"
                        : decision === "REJECTED"
                        ? "rgba(255,68,102,0.03)"
                        : "transparent",
                    transition: "background 0.35s ease",
                  }}
                >
                  <div className="p-4 space-y-3">
                    {/* LP Header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <StatusBadge
                            type={
                              decision === "APPROVED"
                                ? "green"
                                : decision === "REJECTED"
                                ? "red"
                                : "amber"
                            }
                          >
                            {decision || "PENDING REVIEW"}
                          </StatusBadge>
                          {lp.amlFlag && (
                            <StatusBadge type="red">AML FLAG</StatusBadge>
                          )}
                          <StatusBadge type="muted">{lp.tier}</StatusBadge>
                        </div>
                        <div
                          className="mono font-black text-white uppercase"
                          style={{ fontSize: "11px", letterSpacing: "0.06em" }}
                        >
                          {lp.legalName}
                        </div>
                        <div
                          className="mono text-white/35 uppercase mt-0.5"
                          style={{ fontSize: "8px", letterSpacing: "0.1em" }}
                        >
                          {lp.entityType}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div
                          className="mono font-black"
                          style={{
                            fontSize: "14px",
                            color: isPending
                              ? "rgba(255,255,255,0.8)"
                              : decision === "APPROVED"
                              ? "var(--emerald)"
                              : "var(--red-signal)",
                          }}
                        >
                          {fmt.usd(lp.requestedAmount)}
                        </div>
                        <div
                          className="mono text-white/25 uppercase"
                          style={{ fontSize: "7px", letterSpacing: "0.1em" }}
                        >
                          REQUESTED
                        </div>
                      </div>
                    </div>

                    {/* Vault + Metrics Row */}
                    <div
                      className="grid grid-cols-3 gap-2 px-3 py-2"
                      style={{
                        background: "rgba(255,255,255,0.02)",
                        border: "1px solid rgba(255,255,255,0.04)",
                      }}
                    >
                      {[
                        { label: "VAULT", value: lp.vault },
                        {
                          label: "PROJ. RETURN/YR",
                          value: fmt.usd(lp.projectedReturn),
                          em: true,
                        },
                        {
                          label: "ACCRED. BASIS",
                          value: lp.accredBasis.split("—")[0].trim(),
                        },
                      ].map((m, mi) => (
                        <div key={mi}>
                          <div
                            className="mono text-white/20 uppercase"
                            style={{ fontSize: "7px", letterSpacing: "0.1em" }}
                          >
                            {m.label}
                          </div>
                          <div
                            className={cn(
                              "mono font-black uppercase mt-0.5",
                              m.em ? "text-[var(--emerald)]" : "text-white/55"
                            )}
                            style={{ fontSize: "9px" }}
                          >
                            {m.value}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* AML Warning */}
                    {lp.amlFlag && isPending && (
                      <div
                        className="flex items-center gap-2 px-3 py-2"
                        style={{
                          background: "rgba(255,68,102,0.05)",
                          border: "1px solid rgba(255,68,102,0.15)",
                        }}
                      >
                        <Dot color="red" />
                        <span
                          className="mono text-white/50 uppercase"
                          style={{ fontSize: "8px", letterSpacing: "0.12em" }}
                        >
                          AML FLAG DETECTED · MANUAL REVIEW REQUIRED BEFORE
                          APPROVAL
                        </span>
                      </div>
                    )}

                    {/* Decision Buttons */}
                    {isPending ? (
                      <div className="flex items-center gap-2">
                        {/* APPROVE */}
                        <button
                          onClick={() => handleLpDecision(lp.id, "APPROVED")}
                          className="flex-1 flex items-center justify-center gap-2 mono font-black uppercase transition-all duration-200"
                          style={{
                            fontSize: "9px",
                            letterSpacing: "0.18em",
                            padding: "8px",
                            border: "1px solid rgba(0,255,136,0.3)",
                            background: "rgba(0,255,136,0.06)",
                            color: "var(--emerald)",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "rgba(0,255,136,0.14)";
                            e.currentTarget.style.borderColor =
                              "rgba(0,255,136,0.5)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              "rgba(0,255,136,0.06)";
                            e.currentTarget.style.borderColor =
                              "rgba(0,255,136,0.3)";
                          }}
                        >
                          <svg
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                          >
                            <path
                              d="M1 4L3.5 6.5L9 1"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          APPROVE
                        </button>
                        {/* REJECT */}
                        <button
                          onClick={() => handleLpDecision(lp.id, "REJECTED")}
                          className="flex-1 flex items-center justify-center gap-2 mono font-black uppercase transition-all duration-200"
                          style={{
                            fontSize: "9px",
                            letterSpacing: "0.18em",
                            padding: "8px",
                            border: "1px solid rgba(255,68,102,0.2)",
                            background: "rgba(255,68,102,0.04)",
                            color: "var(--red-signal)",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "rgba(255,68,102,0.1)";
                            e.currentTarget.style.borderColor =
                              "rgba(255,68,102,0.4)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              "rgba(255,68,102,0.04)";
                            e.currentTarget.style.borderColor =
                              "rgba(255,68,102,0.2)";
                          }}
                        >
                          <svg
                            width="8"
                            height="8"
                            viewBox="0 0 8 8"
                            fill="none"
                          >
                            <path
                              d="M1 1L7 7M7 1L1 7"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                          REJECT
                        </button>
                        {/* REQUEST DOCS */}
                        <button
                          className="flex items-center justify-center mono text-white/30 uppercase transition-all duration-200"
                          style={{
                            fontSize: "8px",
                            letterSpacing: "0.12em",
                            padding: "8px 10px",
                            border: "1px solid rgba(255,255,255,0.08)",
                            background: "transparent",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color =
                              "rgba(255,255,255,0.55)";
                            e.currentTarget.style.borderColor =
                              "rgba(255,255,255,0.18)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color =
                              "rgba(255,255,255,0.3)";
                            e.currentTarget.style.borderColor =
                              "rgba(255,255,255,0.08)";
                          }}
                        >
                          REQ. DOCS
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Dot
                            color={decision === "APPROVED" ? "emerald" : "red"}
                          />
                          <span
                            className="mono font-black uppercase"
                            style={{
                              fontSize: "9px",
                              letterSpacing: "0.16em",
                              color:
                                decision === "APPROVED"
                                  ? "var(--emerald)"
                                  : "var(--red-signal)",
                            }}
                          >
                            {decision === "APPROVED"
                              ? "SUBSCRIPTION PACKET DISPATCHED"
                              : "REJECTION NOTICE SENT"}
                          </span>
                        </div>
                        <button
                          onClick={() =>
                            setLpDecisions((prev) => {
                              const n = { ...prev };
                              delete n[lp.id];
                              return n;
                            })
                          }
                          className="mono text-white/20 uppercase transition-colors"
                          style={{
                            fontSize: "7px",
                            letterSpacing: "0.1em",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color =
                              "rgba(255,255,255,0.45)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color =
                              "rgba(255,255,255,0.2)")
                          }
                        >
                          UNDO
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </CornerBox>
        </motion.div>
      </div>

      {/* ═══════════════════════════════════════════════════
          COMPLIANCE MATRIX
      ═══════════════════════════════════════════════════ */}
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_HEAVY, delay: 0.38 }}
      >
        <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
          <Label>HUD / SEC COMPLIANCE MATRIX — ALL ACTIVE VAULTS</Label>
          <div className="flex items-center gap-2">
            <VerifiedBadge label="HUD CERTIFIED" />
            <StatusBadge type="green">REG D 506(c) ACTIVE</StatusBadge>
          </div>
        </div>

        <CornerBox className="glass-dark overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>VAULT</th>
                <th>HQS CERTIFICATION</th>
                <th>HAP CONTRACT</th>
                <th>K-1 STATUS</th>
                <th>LEASE STATUS</th>
                <th style={{ textAlign: "right" }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {COMPLIANCE_ROWS.map((row, ri) => (
                <motion.tr
                  key={row.vaultId}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    ...STIFF_INSTITUTIONAL,
                    delay: 0.42 + ri * 0.07,
                  }}
                >
                  {/* Vault ID */}
                  <td>
                    <div className="flex items-center gap-2">
                      <div
                        className="mono font-black flex items-center justify-center shrink-0"
                        style={{
                          width: "28px",
                          height: "28px",
                          border: "1px solid rgba(0,255,136,0.2)",
                          background: "rgba(0,255,136,0.04)",
                          fontSize: "9px",
                          color: "var(--emerald)",
                        }}
                      >
                        {row.shortId}
                      </div>
                      <div>
                        <div
                          className="mono font-black text-white uppercase"
                          style={{ fontSize: "10px" }}
                        >
                          {row.vaultId}
                        </div>
                        <div
                          className="mono text-white/30 uppercase"
                          style={{ fontSize: "8px", letterSpacing: "0.08em" }}
                        >
                          {row.address}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* HQS Cert */}
                  <td>
                    <div>
                      <StatusBadge type={row.hqsCert.type}>
                        {row.hqsCert.status}
                      </StatusBadge>
                      <div
                        className="mono text-white/25 uppercase mt-1"
                        style={{ fontSize: "7px", letterSpacing: "0.08em" }}
                      >
                        {row.hqsCert.expiry}
                      </div>
                    </div>
                  </td>

                  {/* HAP Status */}
                  <td>
                    <div>
                      <StatusBadge type={row.hapStatus.type}>
                        {row.hapStatus.status}
                      </StatusBadge>
                      <div
                        className="mono text-white/25 uppercase mt-1 truncate"
                        style={{
                          fontSize: "7px",
                          letterSpacing: "0.08em",
                          maxWidth: "140px",
                        }}
                      >
                        {row.hapStatus.note}
                      </div>
                    </div>
                  </td>

                  {/* K-1 Status */}
                  <td>
                    <div>
                      <StatusBadge type={row.k1Status.type}>
                        {row.k1Status.status}
                      </StatusBadge>
                      <div
                        className="mono text-white/25 uppercase mt-1"
                        style={{ fontSize: "7px", letterSpacing: "0.08em" }}
                      >
                        {row.k1Status.note}
                      </div>
                    </div>
                  </td>

                  {/* Lease Status */}
                  <td>
                    <StatusBadge
                      type={
                        row.phase === "OPERATIONAL"
                          ? "green"
                          : row.phase === "FUNDING"
                          ? "amber"
                          : "muted"
                      }
                    >
                      {row.phase === "OPERATIONAL"
                        ? "OCCUPIED"
                        : row.phase === "FUNDING"
                        ? "PRE-OCCUPANCY"
                        : "VACANT"}
                    </StatusBadge>
                  </td>

                  {/* Action */}
                  <td style={{ textAlign: "right" }}>
                    <StatusBadge type={row.action.type}>
                      {row.action.status}
                    </StatusBadge>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>

          <div
            className="px-5 py-3 flex items-center justify-between"
            style={{
              borderTop: "1px solid rgba(255,255,255,0.05)",
              background: "rgba(0,255,136,0.02)",
            }}
          >
            <div className="flex items-center gap-2">
              <Dot color="emerald" pulse />
              <span
                className="mono text-white/25 uppercase"
                style={{ fontSize: "8px", letterSpacing: "0.14em" }}
              >
                MATRIX LIVE · LAST SYNC:{" "}
                {new Date().toLocaleTimeString("en-US", { hour12: false })}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {[
                {
                  label: "COMPLIANT",
                  count: COMPLIANCE_ROWS.filter(
                    (r) => r.action.type === "green"
                  ).length,
                  color: "var(--emerald)",
                },
                {
                  label: "ACTION REQ",
                  count: COMPLIANCE_ROWS.filter(
                    (r) => r.action.type === "amber"
                  ).length,
                  color: "var(--amber-signal)",
                },
                {
                  label: "MONITOR",
                  count: COMPLIANCE_ROWS.filter((r) => r.action.type === "gold")
                    .length,
                  color: "var(--gold-signal)",
                },
              ].map((stat, si) => (
                <div key={si} className="flex items-center gap-1.5">
                  <div
                    style={{
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      background: stat.color,
                      boxShadow: `0 0 5px ${stat.color}`,
                    }}
                  />
                  <span
                    className="mono text-white/30 uppercase"
                    style={{ fontSize: "8px", letterSpacing: "0.1em" }}
                  >
                    {stat.count} {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CornerBox>
      </motion.div>

      {/* ═══════════════════════════════════════════════════
          MODULE 3 — VAULT DEPLOYMENT WIZARD
      ═══════════════════════════════════════════════════ */}
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_HEAVY, delay: 0.46 }}
      >
        <div className="mb-3 flex items-center justify-between">
          <Label>
            VAULT DEPLOYMENT WIZARD — LAUNCH NEW 6BR SOVEREIGN FORTRESS
          </Label>
          <button
            onClick={() => setWizardOpen(!wizardOpen)}
            className="mono font-black uppercase transition-all duration-200"
            style={{
              fontSize: "9px",
              letterSpacing: "0.18em",
              padding: "5px 14px",
              border: wizardOpen
                ? "1px solid rgba(0,255,136,0.4)"
                : "1px solid rgba(0,255,136,0.2)",
              background: wizardOpen
                ? "rgba(0,255,136,0.1)"
                : "rgba(0,255,136,0.04)",
              color: "var(--emerald)",
              cursor: "pointer",
            }}
          >
            {wizardOpen ? "▲ COLLAPSE WIZARD" : "▼ LAUNCH WIZARD"}
          </button>
        </div>

        <AnimatePresence>
          {wizardOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ ...SPRING_HEAVY }}
              style={{ overflow: "hidden" }}
            >
              <CornerBox className="glass-dark overflow-hidden">
                {/* Wizard Header */}
                <div
                  className="px-5 py-3 flex items-center justify-between"
                  style={{
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                    background: "rgba(0,255,136,0.02)",
                  }}
                >
                  <div className="flex items-center gap-3">
                    <PhaseBadge>VAULT DEPLOYMENT · REG D 506(c)</PhaseBadge>
                    <StatusBadge type="muted">
                      {wizardStage === "FORM"
                        ? "STAGE: CONFIGURATION"
                        : wizardStage === "DEPLOYING"
                        ? "STAGE: DEPLOYING"
                        : "STAGE: LIVE"}
                    </StatusBadge>
                  </div>
                  <div className="flex items-center gap-4">
                    {[
                      {
                        label: "RAISE TARGET",
                        value: fmt.usd(V2_CONSTANTS.RAISE_PER_VAULT),
                      },
                      {
                        label: "LP SLOTS",
                        value: `${V2_CONSTANTS.LPS_PER_VAULT} × ${fmt.usd(
                          V2_CONSTANTS.LP_MIN_TICKET
                        )}`,
                      },
                      {
                        label: "ORIGINATION FEE",
                        value: fmt.usd(V2_CONSTANTS.ORIGINATION_FEE),
                      },
                    ].map((m, mi) => (
                      <div key={mi} className="text-right">
                        <div
                          className="mono text-white/20 uppercase"
                          style={{ fontSize: "7px", letterSpacing: "0.1em" }}
                        >
                          {m.label}
                        </div>
                        <div
                          className="mono font-black text-white/60"
                          style={{ fontSize: "10px" }}
                        >
                          {m.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Wizard Body */}
                <div className="p-5">
                  <AnimatePresence mode="wait">
                    {/* FORM STAGE */}
                    {wizardStage === "FORM" && (
                      <motion.div
                        key="wform"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Left: Asset Details */}
                          <div className="space-y-3">
                            <Label className="mb-2 block">
                              ASSET CONFIGURATION — 6BR SOVEREIGN FORTRESS
                            </Label>
                            {[
                              {
                                key: "address",
                                label: "PROPERTY STREET ADDRESS",
                                placeholder: "E.G. 4201 BENNING ROAD NE",
                                type: "text",
                              },
                              {
                                key: "city",
                                label: "CITY",
                                placeholder: "WASHINGTON, D.C.",
                                type: "text",
                              },
                              {
                                key: "zip",
                                label: "ZIP CODE",
                                placeholder: "200XX",
                                type: "text",
                              },
                              {
                                key: "sqft",
                                label: "TOTAL SQUARE FOOTAGE",
                                placeholder: "3200",
                                type: "number",
                              },
                            ].map((field) => (
                              <div key={field.key}>
                                <div
                                  className="mono text-white/30 uppercase mb-1.5"
                                  style={{
                                    fontSize: "8px",
                                    letterSpacing: "0.16em",
                                  }}
                                >
                                  {field.label}
                                  {wizardErrors[field.key] && (
                                    <span
                                      className="ml-2"
                                      style={{ color: "var(--red-signal)" }}
                                    >
                                      · {wizardErrors[field.key]}
                                    </span>
                                  )}
                                </div>
                                <input
                                  className="terminal-input"
                                  type={field.type}
                                  placeholder={field.placeholder}
                                  value={wizardForm[field.key]}
                                  onChange={(e) => {
                                    setWizardForm((prev) => ({
                                      ...prev,
                                      [field.key]: e.target.value,
                                    }));
                                    setWizardErrors((prev) => ({
                                      ...prev,
                                      [field.key]: undefined,
                                    }));
                                  }}
                                  style={
                                    wizardErrors[field.key]
                                      ? { borderColor: "rgba(255,68,102,0.4)" }
                                      : {}
                                  }
                                />
                              </div>
                            ))}
                          </div>

                          {/* Right: Financial Config */}
                          <div className="space-y-3">
                            <Label className="mb-2 block">
                              HAP & FINANCIAL PARAMETERS
                            </Label>
                            {[
                              {
                                key: "hapEstimate",
                                label: "ESTIMATED MONTHLY HAP RATE (USD)",
                                placeholder: "8250",
                                type: "number",
                              },
                              {
                                key: "closingEta",
                                label: "ESTIMATED CLOSING / ACTIVATION DATE",
                                placeholder: "E.G. JUNE 15, 2026",
                                type: "text",
                              },
                            ].map((field) => (
                              <div key={field.key}>
                                <div
                                  className="mono text-white/30 uppercase mb-1.5"
                                  style={{
                                    fontSize: "8px",
                                    letterSpacing: "0.16em",
                                  }}
                                >
                                  {field.label}
                                  {wizardErrors[field.key] && (
                                    <span
                                      className="ml-2"
                                      style={{ color: "var(--red-signal)" }}
                                    >
                                      · {wizardErrors[field.key]}
                                    </span>
                                  )}
                                </div>
                                <input
                                  className="terminal-input"
                                  type={field.type}
                                  placeholder={field.placeholder}
                                  value={wizardForm[field.key]}
                                  onChange={(e) => {
                                    setWizardForm((prev) => ({
                                      ...prev,
                                      [field.key]: e.target.value,
                                    }));
                                    setWizardErrors((prev) => ({
                                      ...prev,
                                      [field.key]: undefined,
                                    }));
                                  }}
                                  style={
                                    wizardErrors[field.key]
                                      ? { borderColor: "rgba(255,68,102,0.4)" }
                                      : {}
                                  }
                                />
                              </div>
                            ))}

                            {/* Auto-computed preview */}
                            {wizardForm.hapEstimate &&
                              !isNaN(Number(wizardForm.hapEstimate)) && (
                                <motion.div
                                  initial={{ opacity: 0, y: 4 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className="space-y-2 mt-2"
                                >
                                  <Label>PROJECTED VAULT ECONOMICS</Label>
                                  <CornerBox
                                    className="glass-dark p-3 space-y-2"
                                    style={{
                                      borderColor: "rgba(0,255,136,0.1)",
                                    }}
                                  >
                                    {[
                                      {
                                        label: "GROSS ANNUAL HAP",
                                        value: fmt.usd(
                                          Number(wizardForm.hapEstimate) * 12
                                        ),
                                        em: true,
                                      },
                                      {
                                        label: "LP PREFERRED RETURN",
                                        value: fmt.usd(
                                          V2_CONSTANTS.RAISE_PER_VAULT *
                                            V2_CONSTANTS.NET_YIELD_PCT
                                        ),
                                      },
                                      {
                                        label: "ORIGINATION FEE (GP)",
                                        value: fmt.usd(
                                          V2_CONSTANTS.ORIGINATION_FEE
                                        ),
                                      },
                                      {
                                        label: "RAISE TARGET",
                                        value: fmt.usd(
                                          V2_CONSTANTS.RAISE_PER_VAULT
                                        ),
                                      },
                                    ].map((m, mi) => (
                                      <div
                                        key={mi}
                                        className="flex items-center justify-between"
                                      >
                                        <span
                                          className="mono text-white/30 uppercase"
                                          style={{
                                            fontSize: "8px",
                                            letterSpacing: "0.1em",
                                          }}
                                        >
                                          {m.label}
                                        </span>
                                        <span
                                          className={cn(
                                            "mono font-black uppercase",
                                            m.em
                                              ? "text-[var(--emerald)]"
                                              : "text-white/60"
                                          )}
                                          style={{ fontSize: "10px" }}
                                        >
                                          {m.value}
                                        </span>
                                      </div>
                                    ))}
                                  </CornerBox>
                                </motion.div>
                              )}
                          </div>
                        </div>

                        {/* Wizard Submit */}
                        <div
                          className="flex items-center gap-3 mt-5 pt-4"
                          style={{
                            borderTop: "1px solid rgba(255,255,255,0.05)",
                          }}
                        >
                          <button
                            onClick={handleWizardSubmit}
                            className="flex items-center gap-2 mono font-black uppercase transition-all duration-200"
                            style={{
                              fontSize: "10px",
                              letterSpacing: "0.22em",
                              padding: "11px 22px",
                              border: "1px solid rgba(0,255,136,0.35)",
                              background: "rgba(0,255,136,0.07)",
                              color: "var(--emerald)",
                              cursor: "pointer",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background =
                                "rgba(0,255,136,0.14)";
                              e.currentTarget.style.borderColor =
                                "rgba(0,255,136,0.6)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                "rgba(0,255,136,0.07)";
                              e.currentTarget.style.borderColor =
                                "rgba(0,255,136,0.35)";
                            }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <circle
                                cx="6"
                                cy="6"
                                r="5"
                                stroke="currentColor"
                                strokeWidth="1.2"
                              />
                              <path
                                d="M4 6L5.5 7.5L8.5 4.5"
                                stroke="currentColor"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            DEPLOY VAULT TO EXCHANGE
                          </button>
                          <button
                            onClick={resetWizard}
                            className="mono text-white/30 uppercase transition-colors"
                            style={{
                              fontSize: "9px",
                              letterSpacing: "0.16em",
                              padding: "11px 16px",
                              border: "1px solid rgba(255,255,255,0.07)",
                              background: "none",
                              cursor: "pointer",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color =
                                "rgba(255,255,255,0.55)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color =
                                "rgba(255,255,255,0.3)";
                            }}
                          >
                            CLEAR FORM
                          </button>
                          <div className="flex-1 text-right">
                            <span
                              className="mono text-white/20 uppercase"
                              style={{
                                fontSize: "8px",
                                letterSpacing: "0.12em",
                              }}
                            >
                              VAULT WILL BE ASSIGNED: SSX-VAULT-00
                              {activeVaults.length + 1}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* DEPLOYING STAGE */}
                    {wizardStage === "DEPLOYING" && (
                      <motion.div
                        key="wdeploying"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex flex-col items-center justify-center py-10 space-y-5"
                      >
                        <div
                          className="relative"
                          style={{ width: "72px", height: "72px" }}
                        >
                          <motion.div
                            className="absolute inset-0"
                            style={{
                              border: "1px solid rgba(0,255,136,0.2)",
                              borderRadius: "2px",
                            }}
                            animate={{ rotate: 360 }}
                            transition={{
                              duration: 3,
                              ease: "linear",
                              repeat: Infinity,
                            }}
                          />
                          <motion.div
                            className="absolute"
                            style={{
                              inset: "8px",
                              border: "1px solid transparent",
                              borderTopColor: "var(--emerald)",
                              borderRadius: "1px",
                            }}
                            animate={{ rotate: -360 }}
                            transition={{
                              duration: 1.6,
                              ease: "linear",
                              repeat: Infinity,
                            }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                            >
                              <rect
                                x="2"
                                y="2"
                                width="16"
                                height="16"
                                rx="1"
                                stroke="var(--emerald)"
                                strokeWidth="1"
                                opacity="0.5"
                              />
                              <path
                                d="M6 10H14M10 6V14"
                                stroke="var(--emerald)"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="text-center space-y-2">
                          <div
                            className="mono font-black uppercase"
                            style={{
                              fontSize: "12px",
                              color: "var(--emerald)",
                              letterSpacing: "0.2em",
                            }}
                          >
                            DEPLOYING TO EXCHANGE BOARD
                          </div>
                          <div
                            className="mono text-white/35 uppercase"
                            style={{ fontSize: "9px", letterSpacing: "0.14em" }}
                          >
                            REGISTERING DELAWARE SERIES LLC SLOT
                            <br />
                            INDEXING HAP PARAMETERS · OPENING LP ROSTER
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {[0, 1, 2, 3, 4, 5].map((i) => (
                            <motion.div
                              key={i}
                              className="glow-dot"
                              style={{ width: "5px", height: "5px" }}
                              animate={{ opacity: [0.2, 1, 0.2] }}
                              transition={{
                                duration: 1.4,
                                repeat: Infinity,
                                delay: i * 0.18,
                              }}
                            />
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {/* LIVE STAGE */}
                    {wizardStage === "LIVE" && (
                      <motion.div
                        key="wlive"
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={SPRING_HEAVY}
                        className="py-6 space-y-5"
                      >
                        <div className="text-center space-y-3">
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ ...SPRING_HEAVY, delay: 0.1 }}
                            className="flex justify-center"
                          >
                            <div
                              style={{
                                width: "56px",
                                height: "56px",
                                border: "1px solid rgba(0,255,136,0.45)",
                                background: "rgba(0,255,136,0.07)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 0 28px rgba(0,255,136,0.14)",
                              }}
                            >
                              <svg
                                width="24"
                                height="20"
                                viewBox="0 0 24 20"
                                fill="none"
                              >
                                <motion.path
                                  d="M1 10L8.5 17.5L23 1"
                                  stroke="var(--emerald)"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  initial={{ pathLength: 0 }}
                                  animate={{ pathLength: 1 }}
                                  transition={{
                                    duration: 0.6,
                                    delay: 0.2,
                                    ease: "easeOut",
                                  }}
                                />
                              </svg>
                            </div>
                          </motion.div>
                          <div
                            className="mono font-black uppercase"
                            style={{
                              fontSize: "14px",
                              color: "var(--emerald)",
                              letterSpacing: "0.2em",
                            }}
                          >
                            VAULT LIVE ON EXCHANGE BOARD
                          </div>
                          <div
                            className="mono text-white/40 uppercase"
                            style={{ fontSize: "9px", letterSpacing: "0.14em" }}
                          >
                            SSX-VAULT-00{activeVaults.length + 1} ·{" "}
                            {wizardForm.address.toUpperCase() || "NEW FORTRESS"}{" "}
                            · 6 LP SLOTS OPEN
                          </div>
                        </div>

                        <CornerBox className="glass-dark overflow-hidden">
                          {[
                            {
                              label: "NEW VAULT ID",
                              value: `SSX-VAULT-00${activeVaults.length + 1}`,
                            },
                            {
                              label: "ASSET ADDRESS",
                              value: wizardForm.address.toUpperCase() || "—",
                            },
                            {
                              label: "RAISE TARGET",
                              value: fmt.usd(V2_CONSTANTS.RAISE_PER_VAULT),
                              em: true,
                            },
                            {
                              label: "LP SLOTS OPEN",
                              value: `${V2_CONSTANTS.LPS_PER_VAULT} / ${V2_CONSTANTS.LPS_PER_VAULT}`,
                            },
                            {
                              label: "ORIGINATION FEE GP",
                              value: fmt.usd(V2_CONSTANTS.ORIGINATION_FEE),
                              em: true,
                            },
                            {
                              label: "ESTIMATED CLOSING",
                              value:
                                wizardForm.closingEta.toUpperCase() || "TBD",
                            },
                          ].map((row, i) => (
                            <LedgerRow
                              key={i}
                              label={row.label}
                              value={row.value}
                              highlight={row.em}
                            />
                          ))}
                        </CornerBox>

                        <div className="flex justify-center gap-3">
                          <button
                            onClick={resetWizard}
                            className="flex items-center gap-2 mono font-black uppercase transition-all duration-200"
                            style={{
                              fontSize: "9px",
                              letterSpacing: "0.2em",
                              padding: "10px 20px",
                              border: "1px solid rgba(0,255,136,0.3)",
                              background: "rgba(0,255,136,0.06)",
                              color: "var(--emerald)",
                              cursor: "pointer",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background =
                                "rgba(0,255,136,0.12)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                "rgba(0,255,136,0.06)";
                            }}
                          >
                            DEPLOY ANOTHER VAULT
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </CornerBox>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ═══════════════════════════════════════════════════
          SOVEREIGN CONTROLS — ADMIN COMMAND STRIP
      ═══════════════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_HEAVY, delay: 0.54 }}
      >
        <div className="mb-3">
          <Label>SOVEREIGN CONTROLS — GP ADMINISTRATIVE ACTIONS</Label>
        </div>

        <CornerBox className="glass-dark overflow-hidden">
          <div
            className="px-5 py-3 flex items-center justify-between flex-wrap gap-3"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
          >
            <div className="flex items-center gap-2">
              <Dot color="emerald" pulse />
              <span
                className="mono text-white/30 uppercase"
                style={{ fontSize: "9px", letterSpacing: "0.14em" }}
              >
                ALL SYSTEMS NOMINAL · COMMAND AUTHORITY: XAVIER SNOW · DR.
                JOSEPH ASAMOAH
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <VerifiedBadge label="GP OPERATOR AUTHENTICATED" />
              <StatusBadge type="green">SEC 506(c) ACTIVE</StatusBadge>
            </div>
          </div>

          <div className="p-4 flex items-center flex-wrap gap-3">
            {/* INITIATE ACH BATCH */}
            <button
              onClick={handleAchBatch}
              disabled={achBatchStage !== "IDLE"}
              className="flex items-center gap-2 mono font-black uppercase transition-all duration-200"
              style={{
                fontSize: "9px",
                letterSpacing: "0.18em",
                padding: "9px 16px",
                border:
                  achBatchStage === "CONFIRMED"
                    ? "1px solid rgba(0,255,136,0.5)"
                    : "1px solid rgba(0,255,136,0.25)",
                background:
                  achBatchStage === "CONFIRMED"
                    ? "rgba(0,255,136,0.1)"
                    : "rgba(0,255,136,0.05)",
                color:
                  achBatchStage !== "IDLE"
                    ? "rgba(255,255,255,0.35)"
                    : "var(--emerald)",
                cursor: achBatchStage !== "IDLE" ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (achBatchStage === "IDLE") {
                  e.currentTarget.style.background = "rgba(0,255,136,0.1)";
                  e.currentTarget.style.borderColor = "rgba(0,255,136,0.5)";
                }
              }}
              onMouseLeave={(e) => {
                if (achBatchStage === "IDLE") {
                  e.currentTarget.style.background = "rgba(0,255,136,0.05)";
                  e.currentTarget.style.borderColor = "rgba(0,255,136,0.25)";
                }
              }}
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                <path
                  d="M5.5 1V7M3 4.5L5.5 7L8 4.5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <rect
                  x="1"
                  y="9"
                  width="9"
                  height="1.2"
                  rx="0.5"
                  fill="currentColor"
                  opacity="0.4"
                />
              </svg>
              {achBatchStage === "IDLE"
                ? "INITIATE ACH BATCH"
                : achBatchStage === "PROCESSING"
                ? "PROCESSING…"
                : "BATCH SENT ✓"}
            </button>

            {/* SYNC DCHA LEDGER */}
            <button
              onClick={handleDchaSync}
              disabled={dchaSync !== "IDLE"}
              className="flex items-center gap-2 mono font-black uppercase transition-all duration-200"
              style={{
                fontSize: "9px",
                letterSpacing: "0.18em",
                padding: "9px 16px",
                border:
                  dchaSync === "SYNCED"
                    ? "1px solid rgba(0,170,255,0.45)"
                    : "1px solid rgba(0,170,255,0.2)",
                background:
                  dchaSync === "SYNCED"
                    ? "rgba(0,170,255,0.08)"
                    : "rgba(0,170,255,0.03)",
                color:
                  dchaSync !== "IDLE" ? "rgba(255,255,255,0.35)" : "#00aaff",
                cursor: dchaSync !== "IDLE" ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (dchaSync === "IDLE") {
                  e.currentTarget.style.background = "rgba(0,170,255,0.08)";
                  e.currentTarget.style.borderColor = "rgba(0,170,255,0.4)";
                }
              }}
              onMouseLeave={(e) => {
                if (dchaSync === "IDLE") {
                  e.currentTarget.style.background = "rgba(0,170,255,0.03)";
                  e.currentTarget.style.borderColor = "rgba(0,170,255,0.2)";
                }
              }}
            >
              {dchaSync === "SYNCING" ? (
                <motion.div
                  style={{
                    width: "10px",
                    height: "10px",
                    border: "1.5px solid #00aaff",
                    borderTopColor: "transparent",
                    borderRadius: "50%",
                  }}
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 0.8,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                />
              ) : (
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                  <path
                    d="M1 5.5A4.5 4.5 0 0 1 9.5 3"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M10 5.5A4.5 4.5 0 0 1 1.5 8"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M8 1.5L9.5 3L8 4.5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 6.5L1.5 8L3 9.5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {dchaSync === "IDLE"
                ? "SYNC DCHA LEDGER"
                : dchaSync === "SYNCING"
                ? "SYNCING…"
                : "LEDGER SYNCED ✓"}
            </button>

            {/* GENERATE K-1s */}
            <button
              onClick={handleK1Gen}
              disabled={k1Stage !== "IDLE"}
              className="flex items-center gap-2 mono font-black uppercase transition-all duration-200"
              style={{
                fontSize: "9px",
                letterSpacing: "0.18em",
                padding: "9px 16px",
                border:
                  k1Stage === "COMPLETE"
                    ? "1px solid rgba(245,158,11,0.45)"
                    : "1px solid rgba(245,158,11,0.2)",
                background:
                  k1Stage === "COMPLETE"
                    ? "rgba(245,158,11,0.08)"
                    : "rgba(245,158,11,0.03)",
                color:
                  k1Stage !== "IDLE"
                    ? "rgba(255,255,255,0.35)"
                    : "var(--amber-signal)",
                cursor: k1Stage !== "IDLE" ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (k1Stage === "IDLE") {
                  e.currentTarget.style.background = "rgba(245,158,11,0.08)";
                  e.currentTarget.style.borderColor = "rgba(245,158,11,0.4)";
                }
              }}
              onMouseLeave={(e) => {
                if (k1Stage === "IDLE") {
                  e.currentTarget.style.background = "rgba(245,158,11,0.03)";
                  e.currentTarget.style.borderColor = "rgba(245,158,11,0.2)";
                }
              }}
            >
              <svg width="10" height="12" viewBox="0 0 10 12" fill="none">
                <path
                  d="M1 1H6.5L9 3.5V11H1V1Z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.5 1V3.5H9"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 6H7M3 8H6"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
              </svg>
              {k1Stage === "IDLE"
                ? "GENERATE K-1 PACKAGES"
                : k1Stage === "GENERATING"
                ? "GENERATING…"
                : "K-1s READY ✓"}
            </button>

            {/* EXPORT COMPLIANCE REPORT */}
            <button
              className="flex items-center gap-2 mono font-black uppercase transition-all duration-200"
              style={{
                fontSize: "9px",
                letterSpacing: "0.18em",
                padding: "9px 16px",
                border: "1px solid rgba(255,136,170,0.2)",
                background: "rgba(255,136,170,0.03)",
                color: "#ff88aa",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,136,170,0.07)";
                e.currentTarget.style.borderColor = "rgba(255,136,170,0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,136,170,0.03)";
                e.currentTarget.style.borderColor = "rgba(255,136,170,0.2)";
              }}
            >
              <svg width="10" height="12" viewBox="0 0 10 12" fill="none">
                <path
                  d="M5 1V8M2 5L5 8L8 5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <rect
                  x="1"
                  y="10"
                  width="8"
                  height="1.5"
                  rx="0.5"
                  fill="currentColor"
                  opacity="0.4"
                />
              </svg>
              EXPORT SEC COMPLIANCE REPORT
            </button>

            {/* REFRESH ORACLE */}
            <button
              className="flex items-center gap-2 mono text-white/30 uppercase transition-all duration-200 ml-auto"
              style={{
                fontSize: "8px",
                letterSpacing: "0.14em",
                padding: "9px 14px",
                border: "1px solid rgba(255,255,255,0.07)",
                background: "transparent",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "rgba(255,255,255,0.55)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "rgba(255,255,255,0.3)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
              }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path
                  d="M1 5A4 4 0 0 1 8.5 2.5"
                  stroke="currentColor"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                />
                <path
                  d="M9 5A4 4 0 0 1 1.5 7.5"
                  stroke="currentColor"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                />
                <path
                  d="M7 1L8.5 2.5L7 4"
                  stroke="currentColor"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              REFRESH ORACLE
            </button>
          </div>

          {/* Footer */}
          <div
            className="px-5 py-2.5 flex items-center justify-between"
            style={{
              borderTop: "1px solid rgba(255,255,255,0.04)",
              background: "rgba(0,255,136,0.02)",
            }}
          >
            <span
              className="mono text-white/15 uppercase"
              style={{ fontSize: "8px", letterSpacing: "0.14em" }}
            >
              SSX SOVEREIGN TERMINAL · v4.0.0 · TACTICAL NOIR · GP COMMAND LAYER
            </span>
            <div className="flex items-center gap-3">
              <StatusBadge type="green">DCHA FEED LIVE</StatusBadge>
              <StatusBadge type="green">ORACLE ACTIVE</StatusBadge>
            </div>
          </div>
        </CornerBox>
      </motion.div>
    </ViewShell>
  );
};
// § 10. TERMINAL LAYOUT (Prompts 1 & 2 — Preserved verbatim)
// ─────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  {
    id: "EXCHANGE",
    label: "VAULT EXCHANGE",
    sub: "LIVE MARKET",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect
          x="1"
          y="1"
          width="6"
          height="6"
          rx="0.5"
          stroke="currentColor"
          strokeWidth="1.2"
        />
        <rect
          x="9"
          y="1"
          width="6"
          height="6"
          rx="0.5"
          stroke="currentColor"
          strokeWidth="1.2"
        />
        <rect
          x="1"
          y="9"
          width="6"
          height="6"
          rx="0.5"
          stroke="currentColor"
          strokeWidth="1.2"
        />
        <path
          d="M9 12H15M12 9V15"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: "PORTFOLIO",
    label: "MY PORTFOLIO",
    sub: "LP POSITIONS",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M1 12L5 7L8 10L11 5L15 8"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect
          x="1"
          y="13"
          width="14"
          height="1"
          rx="0.5"
          fill="currentColor"
          opacity="0.4"
        />
      </svg>
    ),
  },
  {
    id: "COMMAND",
    label: "COMMAND",
    sub: "OPERATOR CONSOLE",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect
          x="1"
          y="1"
          width="14"
          height="14"
          rx="1"
          stroke="currentColor"
          strokeWidth="1.2"
        />
        <path
          d="M4 5L7 8L4 11"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 11H12"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: "DOCUMENTS",
    label: "DOC VAULT",
    sub: "PPM · COMPLIANCE",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M3 1H10L13 4V15H3V1Z"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        <path
          d="M10 1V4H13"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        <path
          d="M5 7H11M5 9.5H9"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
];

const TELEMETRY_ITEMS = [
  "SSX-VAULT-001 · OPERATIONAL · HAP ACTIVE",
  "DCHA FY2026 HAP RATE · 6BR · $8,250/MO",
  "SSX-VAULT-002 · FUNDING ROUND OPEN · 2 LP SLOTS REMAINING",
  "PREFERRED RETURN ACCRUAL · 12.50% · ALL ACTIVE POSITIONS",
  "REG D 506(c) · ACCREDITED INVESTORS ONLY · KNOW YOUR LP",
  "TOTAL PLATFORM AUM · $500K DEPLOYED · $900K TARGET",
  "ORIGINATION FEE COLLECTED · $30,000 · VAULT 001",
  "HAP BACKSTOP CONFIRMED · DCHA · U.S. HUD",
  "SSX-VAULT-003 · PIPELINE · HAP APPLICATION SUBMITTED",
  "NEXT ACH BATCH · MAY 01 2026 · Q2 PREFERRED RETURN",
  "SCARCITY ALERT · 2 LP SLOTS REMAINING · VAULT 002",
];

const TopBar = () => {
  const { systemTime, currentUser, systemMetrics } = useSovereign();
  const pad2 = (n) => String(n).padStart(2, "0");
  const formatTime = (d) =>
    `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  const formatDate = (d) => {
    const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const months = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];
    return `${days[d.getDay()]} ${pad2(d.getDate())} ${
      months[d.getMonth()]
    } ${d.getFullYear()}`;
  };
  return (
    <div
      className="glass-topbar flex items-center justify-between px-4 shrink-0"
      style={{ height: "52px" }}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="glow-dot pulse" />
          <span
            className="mono font-black uppercase"
            style={{
              fontSize: "13px",
              letterSpacing: "0.22em",
              color: "var(--emerald)",
            }}
          >
            SSX
          </span>
          <span
            className="mono text-white/25 uppercase"
            style={{ fontSize: "11px", letterSpacing: "0.18em" }}
          >
            SOVEREIGN TERMINAL
          </span>
        </div>
        <div
          style={{
            width: "1px",
            height: "20px",
            background: "rgba(255,255,255,0.08)",
          }}
        />
        <div className="telemetry-chip">
          <Dot color="emerald" pulse />
          <span>SYSTEM LIVE</span>
        </div>
      </div>
      <div
        className="flex-1 mx-6 overflow-hidden"
        style={{ maxWidth: "420px" }}
      >
        <div className="overflow-hidden" style={{ height: "20px" }}>
          <div className="ticker-track">
            <span
              className="mono text-white/25 uppercase"
              style={{ fontSize: "9px", letterSpacing: "0.14em" }}
            >
              {TELEMETRY_ITEMS.join("   ·//·   ")}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="telemetry-chip">
          <span>AUM</span>
          <span className="font-black" style={{ color: "var(--emerald)" }}>
            ${(systemMetrics.totalAUM / 1000).toFixed(0)}K
          </span>
        </div>
        <div className="telemetry-chip">
          <span>VAULTS</span>
          <span className="text-white/70">
            {systemMetrics.operationalVaults} OPS / {systemMetrics.totalVaults}
          </span>
        </div>
        <div
          style={{
            width: "1px",
            height: "20px",
            background: "rgba(255,255,255,0.08)",
          }}
        />
        <div
          className="mono font-black uppercase"
          style={{
            fontSize: "13px",
            color: "var(--emerald)",
            letterSpacing: "0.1em",
            fontVariantNumeric: "tabular-nums",
            minWidth: "72px",
            textAlign: "right",
          }}
        >
          {formatTime(systemTime)}
        </div>
        <div
          className="mono text-white/25 uppercase"
          style={{ fontSize: "9px", letterSpacing: "0.1em" }}
        >
          {formatDate(systemTime)}
        </div>
        <div
          style={{
            width: "1px",
            height: "20px",
            background: "rgba(255,255,255,0.08)",
          }}
        />
        <div className="flex items-center gap-2 pl-1">
          <VerifiedBadge label="ACCRD" />
          <div>
            <div
              className="mono text-white/60 uppercase"
              style={{ fontSize: "9px", letterSpacing: "0.1em" }}
            >
              {currentUser.tier}
            </div>
            <div
              className="mono text-white/90 font-black uppercase"
              style={{ fontSize: "10px", letterSpacing: "0.08em" }}
            >
              {currentUser.displayName.split(" ").slice(0, 2).join(" ")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Sidebar = () => {
  const { activeView, setActiveView, systemMetrics } = useSovereign();
  return (
    <div
      className="glass-sidebar flex flex-col shrink-0"
      style={{ width: "220px" }}
    >
      <div
        className="px-4 pt-5 pb-3"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
      >
        <Label>NAVIGATION // MODULES</Label>
      </div>
      <nav className="flex-1 px-3 pt-3 space-y-0.5">
        {NAV_ITEMS.map((item) => {
          const isActive = activeView === item.id;
          return (
            <button
              key={item.id}
              className={cn("nav-item w-full text-left", isActive && "active")}
              onClick={() => setActiveView(item.id)}
            >
              <span
                style={{
                  color: isActive ? "var(--emerald)" : "rgba(255,255,255,0.35)",
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </span>
              <div className="min-w-0">
                <div style={{ fontSize: "10px" }}>{item.label}</div>
                <div
                  style={{
                    fontSize: "8px",
                    opacity: 0.5,
                    letterSpacing: "0.12em",
                    marginTop: "1px",
                  }}
                >
                  {item.sub}
                </div>
              </div>
            </button>
          );
        })}
      </nav>
      <div
        className="p-3 space-y-2"
        style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
      >
        <CornerBox className="glass-dark p-3">
          <Label>SYSTEM STATUS</Label>
          <div className="mt-2 space-y-1.5">
            {[
              { label: "HAP FEED", status: "LIVE", color: "emerald" },
              {
                label: "ORACLE",
                status: `${systemMetrics.totalVaults} VAULTS`,
                color: "emerald",
              },
              { label: "REG D", status: "506(c) ACTIVE", color: "emerald" },
            ].map((s, i) => (
              <div key={i} className="flex items-center justify-between">
                <span
                  className="mono text-white/25 uppercase"
                  style={{ fontSize: "8px", letterSpacing: "0.12em" }}
                >
                  {s.label}
                </span>
                <div className="flex items-center gap-1.5">
                  <Dot color={s.color} pulse={i === 0} />
                  <span
                    className="mono uppercase"
                    style={{
                      fontSize: "8px",
                      color: "var(--emerald)",
                      letterSpacing: "0.1em",
                    }}
                  >
                    {s.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CornerBox>
        <div
          className="mono text-white/15 uppercase text-center"
          style={{ fontSize: "8px", letterSpacing: "0.12em" }}
        >
          SSX SOVEREIGN TERMINAL
          <br />
          v3.0.0 · TACTICAL NOIR
        </div>
      </div>
    </div>
  );
};

const VIEW_MAP = {
  EXCHANGE: <ExchangeView />,
  PORTFOLIO: <PortfolioView />,
  COMMAND: <CommandView />,
  DOCUMENTS: <DocumentVaultView />,
};

const MainStage = () => {
  const { activeView } = useSovereign();
  return (
    <div className="flex-1 relative overflow-hidden">
      <AnimatePresence mode="wait">
        {VIEW_MAP[activeView] &&
          React.cloneElement(VIEW_MAP[activeView], { key: activeView })}
      </AnimatePresence>
    </div>
  );
};

export const TerminalLayout = () => (
  <div
    className="flex flex-col"
    style={{ height: "100vh", width: "100vw", background: "var(--abyss)" }}
  >
    <TopBar />
    <div className="flex flex-1 overflow-hidden">
      <Sidebar />
      <MainStage />
    </div>
    <div className="scanline-overlay" />
  </div>
);

// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// § 11. APPLICATION ROOT
// ─────────────────────────────────────────────────────────────
export default function App() {
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: GLOBAL_STYLES }} />
      <SovereignProvider>
        <TerminalLayout />
      </SovereignProvider>
    </>
  );
}
